import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import AirportSearch from './AirportSearch';
import CityAutocomplete from './CityAutocomplete';
import ControlledDateInput from './ControlledDateInput';
import DatePickerButton from './DatePickerButton';
import { format } from 'date-fns';
// Load Duffel custom elements (web components) - avoids React version conflicts
import '@duffel/components/custom-elements';
// Stripe Elements for booker card collection (prepaid hotels)
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useParadigm } from '../../contexts/ParadigmContext';

// Initialize Stripe — matches env var pattern used in BillingDashboard
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_TEST_PUBLISHABLE_KEY ||
  ''
);

const BookingSearch = () => {
  const { labels } = useParadigm();
  const [activeTab, setActiveTab] = useState('flights');
  const [flightSearch, setFlightSearch] = useState({
    origin: '',
    destination: '',
    departure_date: '',
    return_date: '',
    adults: 1,
    travel_class: 'ECONOMY'
  });
  const [hotelSearch, setHotelSearch] = useState({
    city: '',
    check_in_date: '',
    check_out_date: '',
    adults: 1,
    room_quantity: 1,
    latitude: null,
    longitude: null
  });
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showHotelBookingModal, setShowHotelBookingModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsType, setTermsType] = useState(''); // 'terms', 'baggage', 'cancellation'
  const [airlineCode, setAirlineCode] = useState(''); // Store airline for specific terms
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState(['balance', 'card']);
  const [flightPaymentMethods, setFlightPaymentMethods] = useState(['balance', 'card']);
  const [hotelPaymentMethods, setHotelPaymentMethods] = useState(['booker_card']);
  const [markupConfig, setMarkupConfig] = useState(null);

  // Fetch org-level allowed payment methods and markup config
  useEffect(() => {
    const fetchDuffelConfig = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const res = await axios.get(`${apiBase}/duffel/mode`, { headers });
        if (res.data.success) {
          if (res.data.payment_methods) {
            setAllowedPaymentMethods(res.data.payment_methods);
          }
          if (res.data.flight_payment_methods) {
            setFlightPaymentMethods(res.data.flight_payment_methods);
          }
          if (res.data.hotel_payment_methods) {
            setHotelPaymentMethods(res.data.hotel_payment_methods);
          }
          if (res.data.markup_config) {
            setMarkupConfig(res.data.markup_config);
          }
        }
      } catch {
        // Default to both
      }
    };
    fetchDuffelConfig();
  }, []);

  const searchFlights = async () => {
    setLoading(true);
    setError('');
    try {
      // Use centralized API helpers
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const response = await axios.get(`${apiBase}/search/flights`, {
        params: flightSearch,
        headers: headers
      });

      if (response.data.success) {
        // Handle nested response structure from Duffel
        const duffelResponse = response.data.data;
        if (duffelResponse.success && duffelResponse.data) {
          setSearchResults(duffelResponse.data);
        } else {
          setError(duffelResponse.error || 'No flights found');
        }
      } else {
        setError(response.data.error || 'Search failed');
      }
    } catch (err) {
      console.error('Flight search error:', err);
      setError('Error searching flights: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const searchHotels = async () => {
    setLoading(true);
    setError('');
    try {
      // Use centralized API helpers
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const params = { ...hotelSearch };
      if (params.latitude == null) delete params.latitude;
      if (params.longitude == null) delete params.longitude;

      const response = await axios.get(`${apiBase}/search/hotels`, {
        params,
        headers: headers
      });

      if (response.data.success) {
        setSearchResults(response.data.data);
      } else {
        setError(response.data.error || 'Search failed');
      }
    } catch (err) {
      console.error('Hotel search error:', err);
      setError('Error searching hotels: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchResults(null);
    switch (activeTab) {
      case 'flights':
        searchFlights();
        break;
      case 'hotels':
        searchHotels();
        break;
      default:
        break;
    }
  };

  return (
    <div className="space-y-6 px-3 sm:px-6 lg:px-8 py-4 sm:py-6 bg-white min-h-screen">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Search & Book</h1>
        <p className="mt-1 text-sm text-gray-500">
          Search for flights and hotels using Duffel API
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'flights', name: 'Flights', icon: '✈️', available: true },
            { id: 'hotels', name: 'Hotels', icon: '🏨', available: true }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              disabled={!tab.available}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : tab.available 
                    ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    : 'border-transparent text-gray-400 cursor-not-allowed'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
            >
              <span>{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Search Forms */}
      <div className="bg-white shadow rounded-lg p-6">
        {activeTab === 'flights' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Origin Airport</label>
                <AirportSearch
                  value={flightSearch.origin}
                  onChange={(value) => setFlightSearch(prev => ({ ...prev, origin: value }))}
                  placeholder="e.g., JFK, New York, Los Angeles"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination Airport</label>
                <AirportSearch
                  value={flightSearch.destination}
                  onChange={(value) => setFlightSearch(prev => ({ ...prev, destination: value }))}
                  placeholder="e.g., LAX, London, Paris"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Departure Date</label>
                <DatePickerButton
                  label={flightSearch.departure_date ? 
                    format(new Date(flightSearch.departure_date), 'MMM dd, yyyy') : 
                    'Select departure date'
                  }
                  selectedDate={flightSearch.departure_date ? new Date(flightSearch.departure_date) : null}
                  onPick={(date) => {
                    const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                    setFlightSearch(prev => ({ ...prev, departure_date: isoDate }));
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Return Date (Optional)</label>
                <DatePickerButton
                  label={flightSearch.return_date ? 
                    format(new Date(flightSearch.return_date), 'MMM dd, yyyy') : 
                    'Select return date'
                  }
                  selectedDate={flightSearch.return_date ? new Date(flightSearch.return_date) : null}
                  onPick={(date) => {
                    const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                    setFlightSearch(prev => ({ ...prev, return_date: isoDate }));
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adults</label>
                <select
                  value={flightSearch.adults}
                  onChange={(e) => setFlightSearch(prev => ({ ...prev, adults: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  {[1,2,3,4,5,6].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Class of Service</label>
              <select
                value={flightSearch.travel_class}
                onChange={(e) => setFlightSearch(prev => ({ ...prev, travel_class: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="ECONOMY">Economy</option>
                <option value="PREMIUM_ECONOMY">Premium Economy</option>
                <option value="BUSINESS">Business</option>
                <option value="FIRST">First Class</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'hotels' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <CityAutocomplete
                value={hotelSearch.city}
                onChange={(value) => setHotelSearch(prev => ({ ...prev, city: value, latitude: null, longitude: null }))}
                onPlaceSelect={async (place) => {
                  if (place.place_id) {
                    try {
                      const response = await axios.get(`${getApiBase()}/places/details`, {
                        params: { place_id: place.place_id },
                        headers: getAuthHeaders()
                      });
                      const geometry = response.data?.result?.geometry;
                      if (geometry?.location) {
                        setHotelSearch(prev => ({
                          ...prev,
                          latitude: geometry.location.lat,
                          longitude: geometry.location.lng
                        }));
                      }
                    } catch (err) {
                      console.warn('Could not fetch place details for coordinates:', err);
                    }
                  }
                }}
                types={[]}
                placeholder="e.g., Paris, JFK, 123 Main St Denver"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date</label>
                <DatePickerButton
                  label={hotelSearch.check_in_date ? 
                    format(new Date(hotelSearch.check_in_date), 'MMM dd, yyyy') : 
                    'Select check-in date'
                  }
                  selectedDate={hotelSearch.check_in_date ? new Date(hotelSearch.check_in_date) : null}
                  onPick={(date) => {
                    const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                    setHotelSearch(prev => ({ ...prev, check_in_date: isoDate }));
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date</label>
                <DatePickerButton
                  label={hotelSearch.check_out_date ? 
                    format(new Date(hotelSearch.check_out_date), 'MMM dd, yyyy') : 
                    'Select check-out date'
                  }
                  selectedDate={hotelSearch.check_out_date ? new Date(hotelSearch.check_out_date) : null}
                  onPick={(date) => {
                    const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                    setHotelSearch(prev => ({ ...prev, check_out_date: isoDate }));
                  }}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Adults</label>
                <select
                  value={hotelSearch.adults}
                  onChange={(e) => setHotelSearch(prev => ({ ...prev, adults: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  {[1,2,3,4,5,6,7,8].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Adult' : 'Adults'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Rooms</label>
                <select
                  value={hotelSearch.room_quantity}
                  onChange={(e) => setHotelSearch(prev => ({ ...prev, room_quantity: parseInt(e.target.value) }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  {[1,2,3,4,5].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Room' : 'Rooms'}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="pt-4">
          <button
            onClick={handleSearch}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Search Results */}
      {searchResults && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {activeTab === 'flights' && 'Available Flights'}
            {activeTab === 'hotels' && 'Available Hotels'}
          </h3>
          
          {activeTab === 'flights' && (
            <FlightResults 
              results={searchResults} 
              onSelectFlight={(flight) => {
                setSelectedFlight(flight);
                setShowBookingModal(true);
              }}
            />
          )}
          
          {activeTab === 'hotels' && searchResults && (
            <HotelResults
              results={searchResults}
              searchParams={hotelSearch}
              onSelectHotel={(hotel) => {
                setSelectedHotel(hotel);
                setShowHotelBookingModal(true);
              }}
            />
          )}
        </div>
      )}

      {/* Flight Booking Modal */}
      {showBookingModal && selectedFlight && (
        <BookingModal
          flight={selectedFlight}
          allowedPaymentMethods={flightPaymentMethods}
          markupConfig={markupConfig}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedFlight(null);
          }}
          onShowTerms={(type, airline) => {
            setTermsType(type);
            setAirlineCode(airline);
            setShowTermsModal(true);
          }}
        />
      )}

      {/* Hotel Booking Modal */}
      {showHotelBookingModal && selectedHotel && (
        <HotelBookingModal
          hotel={selectedHotel}
          searchParams={hotelSearch}
          markupConfig={markupConfig}
          hotelPaymentMethods={hotelPaymentMethods}
          onClose={() => {
            setShowHotelBookingModal(false);
            setSelectedHotel(null);
          }}
        />
      )}

      {/* Terms Modal */}
      {showTermsModal && (
        <TermsModal 
          type={termsType}
          airlineCode={airlineCode}
          onClose={() => setShowTermsModal(false)}
        />
      )}
    </div>
  );
};

// ---- Stripe Card Form for booker card collection (prepaid hotels) ----
const StripeCardFormInner = ({ clientSecret, onSuccess, onError, buttonLabel = 'Pay Now', loading = false }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setCardError('');

    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) {
        setCardError(result.error.message);
        onError?.(result.error);
      } else if (result.paymentIntent?.status === 'succeeded') {
        onSuccess?.(result.paymentIntent);
      }
    } catch (err) {
      setCardError(err.message || 'Payment failed');
      onError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="border border-gray-300 rounded-md p-3 mb-3">
        <CardElement options={{
          style: { base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } } },
          hidePostalCode: true,
        }} />
      </div>
      {cardError && <p className="text-red-600 text-sm mb-2">{cardError}</p>}
      <button type="submit" disabled={!stripe || processing || loading}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-4 rounded-md font-medium text-lg transition-colors">
        {processing ? 'Processing...' : buttonLabel}
      </button>
    </form>
  );
};

const StripeCardForm = ({ clientSecret, onSuccess, onError, buttonLabel, loading }) => {
  if (!clientSecret) return null;
  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <StripeCardFormInner clientSecret={clientSecret} onSuccess={onSuccess} onError={onError} buttonLabel={buttonLabel} loading={loading} />
    </Elements>
  );
};

// ---- Saved Cards Selector ----
const SavedCardsSelector = ({ cards, selectedCardId, onSelect, onAddNew }) => {
  if (!cards || cards.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2 mb-3">
      <h6 className="text-sm font-medium text-gray-700">Your Saved Cards</h6>
      {cards.map(card => (
        <label key={card.id}
          className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${selectedCardId === card.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
          <input type="radio" name="savedCard" value={card.id}
            checked={selectedCardId === card.id}
            onChange={() => onSelect(card.id)}
            className="mr-3" />
          <div className="flex items-center flex-1">
            <span className="font-medium text-sm capitalize">{card.brand || 'Card'}</span>
            <span className="text-sm text-gray-500 ml-2">**** {card.last4}</span>
            {card.exp_month && card.exp_year && (
              <span className="text-xs text-gray-400 ml-2">{card.exp_month}/{card.exp_year}</span>
            )}
            {card.is_default && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-2">Default</span>
            )}
            <span className="text-xs text-gray-400 ml-auto capitalize">{card.card_type}</span>
          </div>
        </label>
      ))}
      <button onClick={onAddNew}
        className="flex items-center p-3 border border-dashed border-gray-300 rounded-lg w-full hover:bg-gray-50 text-sm text-gray-600">
        <span className="mr-2">+</span> Use a new card
      </button>
    </div>
  );
};

// Wrapper for <duffel-payments> web component (avoids React 18/19 conflict)
const DuffelPaymentsElement = ({ clientToken, onSuccess, onError }) => {
  const containerRef = useRef(null);
  const elRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create the custom element and append it
    const el = document.createElement('duffel-payments');
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(el);
    elRef.current = el;

    // Listen for payment events
    const handleSuccess = (e) => onSuccess?.(e.detail);
    const handleError = (e) => onError?.(e.detail?.error || e.detail);

    el.addEventListener('onSuccessfulPayment', handleSuccess);
    el.addEventListener('onFailedPayment', handleError);

    // Call render() with props - this is how the Duffel web component works
    // It needs render() called after connectedCallback fires
    requestAnimationFrame(() => {
      if (el.render) {
        el.render({ paymentIntentClientToken: clientToken });
      }
    });

    return () => {
      el.removeEventListener('onSuccessfulPayment', handleSuccess);
      el.removeEventListener('onFailedPayment', handleError);
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [clientToken, onSuccess, onError]);

  return <div ref={containerRef} className="mt-3 border border-gray-200 rounded-lg overflow-visible" style={{ minHeight: '350px' }} />;
};

// Build airline logo URL from Duffel's CDN — works for any IATA code
const getDuffelLogoUrl = (code) => code ? `https://assets.duffel.com/img/airlines/for-light-background/full-color-logo/${code}.svg` : null;
const getDuffelLockupUrl = (code) => code ? `https://assets.duffel.com/img/airlines/for-light-background/full-color-lockup/${code}.svg` : null;

// Get airline info from segment data (which now includes carrierName/carrierLogoUrl from Duffel API)
// Falls back to Duffel CDN URL pattern if logo not in segment data
const getAirlineInfo = (carrierCode, segment) => ({
  name: (segment && segment.carrierName) || carrierCode,
  logo: (segment && segment.carrierLogoUrl) || getDuffelLogoUrl(carrierCode),
  lockup: (segment && segment.carrierLockupUrl) || getDuffelLockupUrl(carrierCode),
});

// Airline logo component with fallback to code text
const AirlineLogo = ({ code, segment, size = 'sm', showName = true, className = '' }) => {
  const info = getAirlineInfo(code, segment);
  const sizeClasses = size === 'lg' ? 'h-8 w-8' : size === 'md' ? 'h-6 w-6' : 'h-5 w-5';
  const textClass = size === 'lg' ? 'text-sm' : 'text-xs';
  return (
    <div className={`flex items-center space-x-1.5 ${className}`}>
      {info.logo ? (
        <img
          src={info.logo}
          alt={info.name}
          className={`${sizeClasses} object-contain`}
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
        />
      ) : null}
      <span
        className={`${sizeClasses} bg-gray-200 rounded-full items-center justify-center text-gray-600 font-bold text-[10px]`}
        style={{ display: info.logo ? 'none' : 'flex' }}
      >
        {code}
      </span>
      {showName && <span className={`${textClass} text-gray-600 font-medium`}>{info.name}</span>}
    </div>
  );
};

// Booking Modal Component
const BookingModal = ({ flight, onClose, onShowTerms, allowedPaymentMethods = ['credit_card'], markupConfig = null }) => {
  const { labels } = useParadigm();
  const [bookingStep, setBookingStep] = useState('passengers'); // 'passengers', 'extras', 'review', 'success'
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const flightMethods = allowedPaymentMethods || ['credit_card'];
  const [paymentMethod, setPaymentMethod] = useState(flightMethods[0] || 'credit_card');
  const [paymentIntentClientToken, setPaymentIntentClientToken] = useState(null);
  const [cardPaymentReady, setCardPaymentReady] = useState(false);

  // Saved cards state for flights
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedCardId, setSelectedSavedCardId] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [saveCardForFuture, setSaveCardForFuture] = useState(true);

  // Fetch saved cards on mount
  useEffect(() => {
    const fetchSavedCards = async () => {
      try {
        const response = await axios.get(`${getApiBase()}/saved-cards`, { headers: getAuthHeaders() });
        if (response.data.success) {
          setSavedCards(response.data.data || []);
          const defaultCard = (response.data.data || []).find(c => c.is_default);
          if (defaultCard) setSelectedSavedCardId(defaultCard.id);
        }
      } catch (err) {
        console.error('Failed to load saved cards:', err);
      }
    };
    fetchSavedCards();
  }, []);

  // Ancillary services state
  const [availableServices, setAvailableServices] = useState(null);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]); // [{id, quantity}]

  // Executive auto-fill state
  const [executives, setExecutives] = useState([]);
  const [executivesLoading, setExecutivesLoading] = useState(true);

  // Passenger count (Duffel offers are priced per-passenger, count from travelerPricings)
  const passengerCount = flight.travelerPricings ? flight.travelerPricings.length : 1;
  const [passengers, setPassengers] = useState(
    Array.from({ length: passengerCount }, () => ({
      given_name: '',
      family_name: '',
      email: '',
      phone_number: '',
      gender: 'm',
      title: 'mr',
      born_on: '',
      type: 'adult',
      _executive_id: '', // Track which executive was selected
    }))
  );

  // Fetch booking-ready executives on mount
  useEffect(() => {
    const fetchExecutives = async () => {
      try {
        const response = await axios.get(`${getApiBase()}/executives/booking-ready`, { headers: getAuthHeaders() });
        setExecutives(response.data.executives || []);
      } catch (err) {
        console.error('Failed to load executives:', err);
      } finally {
        setExecutivesLoading(false);
      }
    };
    fetchExecutives();
  }, []);

  // Auto-fill a passenger slot from executive data
  const fillFromExecutive = (passengerIndex, executiveId) => {
    if (!executiveId) {
      // Clear the auto-fill
      setPassengers(prev => {
        const updated = [...prev];
        updated[passengerIndex] = {
          given_name: '', family_name: '', email: '', phone_number: '',
          gender: 'm', title: 'mr', born_on: '', type: 'adult', _executive_id: ''
        };
        return updated;
      });
      return;
    }

    const exec = executives.find(e => e.id === executiveId);
    if (!exec) return;

    setPassengers(prev => {
      const updated = [...prev];
      updated[passengerIndex] = {
        given_name: exec.first_name || '',
        family_name: exec.last_name || '',
        email: exec.email || '',
        phone_number: exec.phone || '',
        gender: exec.duffel_gender || 'm',
        title: exec.duffel_title || 'mr',
        born_on: exec.date_of_birth || '',
        type: 'adult',
        _executive_id: executiveId,
      };
      return updated;
    });
  };

  // Fetch available services when moving to extras step
  const handleGoToExtras = async () => {
    setBookingStep('extras');
    if (availableServices) return; // Already loaded
    setServicesLoading(true);
    try {
      const offerId = flight.duffel_offer_id || flight.id;
      const response = await axios.get(`${getApiBase()}/offers/${offerId}/services`, { headers: getAuthHeaders() });
      if (response.data.success) {
        setAvailableServices(response.data.data);
      } else {
        setAvailableServices({ baggage: [], seats: [], other: [] });
      }
    } catch (err) {
      console.error('Failed to load ancillary services:', err);
      setAvailableServices({ baggage: [], seats: [], other: [] });
    } finally {
      setServicesLoading(false);
    }
  };

  const toggleService = (serviceId, maxQty = 1) => {
    setSelectedServices(prev => {
      const existing = prev.find(s => s.id === serviceId);
      if (existing) {
        return prev.filter(s => s.id !== serviceId);
      }
      return [...prev, { id: serviceId, quantity: 1 }];
    });
  };

  const updateServiceQuantity = (serviceId, quantity) => {
    setSelectedServices(prev =>
      prev.map(s => s.id === serviceId ? { ...s, quantity } : s)
    );
  };

  // Calculate total extras cost
  const extrasCost = selectedServices.reduce((total, sel) => {
    if (!availableServices) return total;
    const allSvcs = [...(availableServices.baggage || []), ...(availableServices.seats || []), ...(availableServices.other || [])];
    const svc = allSvcs.find(s => s.service_id === sel.id);
    if (svc) {
      total += parseFloat(svc.total_amount || 0) * (sel.quantity || 1);
    }
    return total;
  }, 0);

  // Calculate markup amounts — supports both new (unified) and legacy field names
  const calcMarkup = (amount, type, value, flat, pct, min, max) => {
    if (!type || type === 'none') return 0;
    let fee = 0;
    const amt = parseFloat(amount) || 0;
    if (type === 'flat') fee = flat != null ? flat : (value || 0);
    else if (type === 'percentage') fee = amt * ((pct != null ? pct : (value || 0)) / 100);
    else if (type === 'both') fee = (flat || 0) + amt * ((pct || 0) / 100);
    else return 0;
    if (min > 0) fee = Math.max(fee, min);
    if (max > 0) fee = Math.min(fee, max);
    return Math.round(fee * 100) / 100;
  };

  const mc = markupConfig || {};
  // New field names with legacy fallback
  const flightFeeType = mc.flight_booking_fee_type || mc.flight_markup_type || 'none';
  const flightMarkup = markupConfig
    ? calcMarkup(
        flight.price.total,
        flightFeeType,
        mc.flight_markup_value,
        mc.flight_booking_fee_flat,
        mc.flight_booking_fee_pct,
        mc.flight_booking_fee_min || 0,
        mc.flight_booking_fee_max || 0,
      )
    : 0;

  const ancMarkupType = mc.ancillary_markup_type || 'none';
  const ancillaryMarkup = (markupConfig && extrasCost > 0)
    ? calcMarkup(extrasCost, ancMarkupType, mc.ancillary_markup_value, mc.ancillary_markup_value, mc.ancillary_markup_value, 0, 0)
    : 0;

  const bookingFee = mc.flight_booking_fee_flat || mc.booking_fee || 0;
  // Only add bookingFee if type is "flat" and it isn't already in flightMarkup
  const extraFlatFee = (flightFeeType === 'percentage' || flightFeeType === 'none') ? bookingFee : 0;
  const totalMarkup = flightMarkup + ancillaryMarkup + extraFlatFee;
  const showMarkupLine = (mc.show_booking_fee_separately || mc.show_markup_as_separate_line) && totalMarkup > 0;
  const markupLabel = mc.booking_fee_label || mc.markup_label || 'Booking Fee';

  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const allItineraries = flight.itineraries;
  const isRoundTrip = allItineraries.length > 1;
  const allSegments = allItineraries.flatMap(it => it.segments);
  const segments = allItineraries[0].segments; // outbound segments (for backward compat)
  const price = flight.price;
  const primaryCarrier = segments[0].carrierCode;
  const routeLabel = isRoundTrip
    ? `${segments[0].departure.iataCode} \u2192 ${segments[segments.length - 1].arrival.iataCode} \u2192 ${segments[0].departure.iataCode}`
    : `${segments[0].departure.iataCode} \u2192 ${segments[segments.length - 1].arrival.iataCode}`;

  const currencySymbol = price.currency === 'USD' ? '$' : price.currency === 'EUR' ? '\u20AC' : price.currency === 'GBP' ? '\u00A3' : price.currency + ' ';

  const formatTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return new Date(dateTime).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateTime) => {
    if (!dateTime) return 'N/A';
    return new Date(dateTime).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDurationInline = (duration) => {
    if (!duration) return '';
    return duration.replace('PT', '').replace('H', 'h ').replace('M', 'm');
  };

  const updatePassenger = (index, field, value) => {
    setPassengers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const isPassengerFormValid = () => {
    return passengers.every(p => p.given_name.trim() && p.family_name.trim() && p.born_on && p.phone_number.trim());
  };

  const buildBookingPayload = () => {
    const offerId = flight.duffel_offer_id || flight.id;
    const duffelPassengers = passengers.map((pax, i) => {
      const tp = flight.travelerPricings?.[i];
      const { _executive_id, ...paxData } = pax;
      return { ...paxData, id: tp?.travelerId || undefined };
    });

    return {
      offer_id: offerId,
      passengers: duffelPassengers,
      amount: price.total,
      currency: price.currency,
      payment_method: paymentMethod,
      flight_info: {
        airline: allSegments[0].carrierCode,
        flight_number: allSegments[0].number,
        departure_airport: allSegments[0].departure.iataCode,
        arrival_airport: allSegments[allSegments.length - 1].arrival.iataCode,
        departure_date: allSegments[0].departure.at,
        arrival_date: allSegments[allSegments.length - 1].arrival.at,
        class_type: flight.travelerPricings?.[0]?.fareDetailsBySegment?.[0]?.cabin || 'ECONOMY',
        is_round_trip: isRoundTrip,
      },
      segments: allSegments.map((seg, i) => ({
        carrierCode: seg.carrierCode,
        number: seg.number,
        departure_iataCode: seg.departure.iataCode,
        departure_at: seg.departure.at,
        departure_terminal: seg.departure.terminal || '',
        arrival_iataCode: seg.arrival.iataCode,
        arrival_at: seg.arrival.at,
        arrival_terminal: seg.arrival.terminal || '',
        aircraft_code: seg.aircraft?.code || '',
        duration: seg.duration || '',
        cabin: flight.travelerPricings?.[0]?.fareDetailsBySegment?.[i]?.cabin || 'ECONOMY',
      })),
      services: selectedServices.length > 0 ? selectedServices : undefined,
      markup: totalMarkup > 0 ? {
        flight_markup: flightMarkup,
        ancillary_markup: ancillaryMarkup,
        booking_fee: bookingFee,
        total_markup: totalMarkup,
        markup_config_snapshot: markupConfig,
      } : undefined,
      conditions: flight.conditions || undefined,
    };
  };

  // Create a Duffel payment intent for card payments
  const handleSelectCardPayment = async () => {
    setCardPaymentReady(false);
    setBookingError('');
    setBookingLoading(true);

    try {
      // Payment intent should cover the full customer price (base + extras + markup)
      const customerTotal = (parseFloat(price.total) + extrasCost + totalMarkup).toFixed(2);
      const response = await axios.post(`${apiBase}/payments/duffel/create-intent`, {
        amount: customerTotal,
        currency: price.currency,
      }, { headers });

      if (response.data.success && response.data.data.client_token) {
        setPaymentIntentClientToken(response.data.data.client_token);
      } else {
        setBookingError(response.data.error || 'Failed to initialize card payment. Please try again.');
      }
    } catch (err) {
      console.error('Payment intent error:', err);
      setBookingError('Failed to initialize card payment. Try again or use company account.');
    } finally {
      setBookingLoading(false);
    }
  };

  // Called by DuffelPayments component when card collection succeeds
  const handleCardPaymentSuccess = useCallback(async (metadata) => {
    setCardPaymentReady(true);
    if (!termsAccepted) {
      setBookingError('Please accept the terms and conditions before proceeding.');
      return;
    }
    // Proceed to book automatically after card is collected
    setBookingLoading(true);
    setBookingError('');

    try {
      const bookingPayload = buildBookingPayload();
      // For card payments, the payment intent was already created and card collected
      bookingPayload.payment_intent_id = metadata?.id || paymentIntentClientToken;

      const response = await axios.post(`${apiBase}/bookings/flights`, bookingPayload, { headers });
      if (response.data.success) {
        setConfirmationNumber(response.data.confirmation_number || response.data.booking_reference || '');
        setBookingStep('success');
      } else {
        setBookingError(response.data.error || 'Booking failed. Please try again.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      setBookingError(error.response?.data?.error || error.response?.data?.detail || 'Unable to complete booking.');
    } finally {
      setBookingLoading(false);
    }
  }, [paymentIntentClientToken, termsAccepted]);

  const handleBookFlight = async () => {
    if (paymentMethod === 'card') {
      // Card payments are handled by the DuffelPayments component flow
      // The user should use the card form below instead
      setBookingError('Please complete the card payment form below.');
      return;
    }

    setBookingLoading(true);
    setBookingError('');

    try {
      const bookingPayload = buildBookingPayload();
      const response = await axios.post(`${apiBase}/bookings/flights`, bookingPayload, { headers });

      if (response.data.success) {
        setConfirmationNumber(response.data.confirmation_number || response.data.booking_reference || '');
        setBookingStep('success');
      } else {
        setBookingError(response.data.error || 'Booking failed. Please try again.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      setBookingError(error.response?.data?.error || error.response?.data?.detail || 'Unable to complete booking. Please try again or contact support.');
    } finally {
      setBookingLoading(false);
    }
  };

  // ---- SUCCESS STEP ----
  if (bookingStep === 'success') {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <span className="text-green-600 text-2xl">&#10003;</span>
            </div>
            <h3 className="text-2xl font-medium text-gray-900 mb-2">Booking Confirmed!</h3>
            {confirmationNumber && (
              <p className="text-lg font-mono text-blue-600 mb-4">
                Confirmation: {confirmationNumber}
              </p>
            )}
            <p className="text-gray-600 mb-2">
              {segments[0].departure.iataCode} &rarr; {segments[segments.length - 1].arrival.iataCode}
            </p>
            <p className="text-gray-500 text-sm mb-6">
              {passengers.map(p => `${p.given_name} ${p.family_name}`).join(', ')}
            </p>
            <div className="space-y-3">
              <button
                onClick={onClose}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-md font-medium text-lg transition-colors"
              >
                Done
              </button>
              <Link to="/bookings"
                className="block w-full text-center text-blue-600 hover:text-blue-800 py-2 text-sm font-medium">
                View All Bookings
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- PASSENGERS STEP ----
  if (bookingStep === 'passengers') {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
        <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white mb-10">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-medium text-gray-900">Passenger Details</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
          </div>

          {/* Flight summary bar */}
          <div className="bg-blue-50 rounded-lg p-3 mb-6 flex items-center justify-between text-sm">
            <div className="flex items-center">
              <AirlineLogo code={segments[0].carrierCode} segment={segments[0]} size="md" showName={false} />
              <span className="font-medium ml-2">{segments[0].carrierCode} {segments[0].number}</span>
              <span className="mx-2 text-gray-400">|</span>
              <span>{routeLabel}</span>
              <span className="mx-2 text-gray-400">|</span>
              <span>{formatDate(segments[0].departure.at)}{isRoundTrip ? ` - ${formatDate(allItineraries[1].segments[0].departure.at)}` : ''}</span>
            </div>
            <div className="font-bold text-blue-600">{currencySymbol}{price.total}</div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            {passengerCount} passenger{passengerCount > 1 ? 's' : ''} &mdash; select an executive to auto-fill or enter details manually.
          </p>

          {passengers.map((pax, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-800">Passenger {index + 1}</h4>
                {pax._executive_id && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Auto-filled</span>
                )}
              </div>

              {/* Executive selector */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{labels.fillFromExecutive}</label>
                <select
                  value={pax._executive_id || ''}
                  onChange={e => fillFromExecutive(index, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="">-- Enter manually --</option>
                  {executivesLoading && <option disabled>Loading executives...</option>}
                  {executives.map(exec => (
                    <option
                      key={exec.id}
                      value={exec.id}
                      disabled={passengers.some((p, pi) => pi !== index && p._executive_id === exec.id)}
                    >
                      {exec.first_name} {exec.last_name} {exec.booking_ready ? '' : '(incomplete profile)'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Show missing field warnings */}
              {pax._executive_id && (() => {
                const exec = executives.find(e => e.id === pax._executive_id);
                if (exec && exec.missing_fields.length > 0) {
                  return (
                    <div className="mb-3 bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800">
                      Missing from profile: {exec.missing_fields.join(', ')}. Please fill in below.
                    </div>
                  );
                }
                return null;
              })()}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <select value={pax.title} onChange={e => updatePassenger(index, 'title', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="mr">Mr</option>
                    <option value="ms">Ms</option>
                    <option value="mrs">Mrs</option>
                    <option value="miss">Miss</option>
                    <option value="dr">Dr</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <select value={pax.gender} onChange={e => updatePassenger(index, 'gender', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="m">Male</option>
                    <option value="f">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" value={pax.given_name} onChange={e => updatePassenger(index, 'given_name', e.target.value)}
                    placeholder="As shown on ID" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" value={pax.family_name} onChange={e => updatePassenger(index, 'family_name', e.target.value)}
                    placeholder="As shown on ID" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                  <input type="date" value={pax.born_on} onChange={e => updatePassenger(index, 'born_on', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={pax.email} onChange={e => updatePassenger(index, 'email', e.target.value)}
                    placeholder="For booking confirmation" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                  <input type="tel" value={pax.phone_number} onChange={e => updatePassenger(index, 'phone_number', e.target.value)}
                    placeholder="+1 555 123 4567" required className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
            </div>
          ))}

          <div className="flex justify-between mt-4">
            <button onClick={onClose}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-6 rounded-md font-medium transition-colors">
              Cancel
            </button>
            <button onClick={handleGoToExtras} disabled={!isPassengerFormValid()}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 px-6 rounded-md font-medium transition-colors">
              Continue to Extras
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- EXTRAS STEP (Bags, Seats, etc.) ----
  if (bookingStep === 'extras') {
    const hasBaggage = availableServices?.baggage?.length > 0;
    const hasSeats = availableServices?.seats?.length > 0;
    const hasOther = availableServices?.other?.length > 0;
    const hasAnyServices = hasBaggage || hasSeats || hasOther;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
        <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white mb-10">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-medium text-gray-900">Add Extras</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
          </div>

          {/* Flight summary bar */}
          <div className="bg-blue-50 rounded-lg p-3 mb-6 flex items-center justify-between text-sm">
            <div className="flex items-center">
              <AirlineLogo code={segments[0].carrierCode} segment={segments[0]} size="md" showName={false} />
              <span className="font-medium ml-2">{segments[0].carrierCode} {segments[0].number}</span>
              <span className="mx-2 text-gray-400">|</span>
              <span>{routeLabel}</span>
            </div>
            <div className="font-bold text-blue-600">{currencySymbol}{price.total}</div>
          </div>

          {servicesLoading && (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
              Loading available extras...
            </div>
          )}

          {!servicesLoading && !hasAnyServices && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">No additional services available for this flight.</p>
              <p className="text-sm text-gray-400">You can proceed directly to review.</p>
            </div>
          )}

          {!servicesLoading && hasBaggage && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-800 mb-3">Checked Bags</h4>
              <div className="space-y-2">
                {availableServices.baggage.map(svc => {
                  const isSelected = selectedServices.some(s => s.id === svc.service_id);
                  const selectedSvc = selectedServices.find(s => s.id === svc.service_id);
                  const weightInfo = svc.metadata?.maximum_weight_kg ? `${svc.metadata.maximum_weight_kg}kg` : '';
                  const bagType = svc.metadata?.type === 'checked' ? 'Checked bag' : svc.metadata?.type || 'Bag';
                  return (
                    <div key={svc.service_id} className={`border rounded-lg p-3 flex items-center justify-between cursor-pointer transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                      onClick={() => toggleService(svc.service_id, svc.maximum_quantity)}>
                      <div className="flex items-center">
                        <input type="checkbox" checked={isSelected} readOnly className="h-4 w-4 text-blue-600 rounded mr-3" />
                        <div>
                          <div className="text-sm font-medium">{bagType} {weightInfo && `(${weightInfo})`}</div>
                          <div className="text-xs text-gray-500">Per passenger, per segment</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {isSelected && svc.maximum_quantity > 1 && (
                          <select
                            value={selectedSvc?.quantity || 1}
                            onChange={(e) => { e.stopPropagation(); updateServiceQuantity(svc.service_id, parseInt(e.target.value)); }}
                            onClick={(e) => e.stopPropagation()}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            {Array.from({ length: svc.maximum_quantity }, (_, i) => i + 1).map(n => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        )}
                        <span className="font-medium text-blue-600">{currencySymbol}{svc.total_amount}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!servicesLoading && hasOther && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-800 mb-3">Other Services</h4>
              <div className="space-y-2">
                {availableServices.other.map(svc => {
                  const isSelected = selectedServices.some(s => s.id === svc.service_id);
                  return (
                    <div key={svc.service_id} className={`border rounded-lg p-3 flex items-center justify-between cursor-pointer transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                      onClick={() => toggleService(svc.service_id)}>
                      <div className="flex items-center">
                        <input type="checkbox" checked={isSelected} readOnly className="h-4 w-4 text-blue-600 rounded mr-3" />
                        <div className="text-sm font-medium">{svc.type}</div>
                      </div>
                      <span className="font-medium text-blue-600">{currencySymbol}{svc.total_amount}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Extras total */}
          {selectedServices.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex justify-between items-center">
              <span className="text-sm font-medium text-green-800">{selectedServices.length} extra{selectedServices.length > 1 ? 's' : ''} selected</span>
              <span className="font-bold text-green-700">+{currencySymbol}{extrasCost.toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between mt-4">
            <button onClick={() => setBookingStep('passengers')}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-6 rounded-md font-medium transition-colors">
              Back
            </button>
            <button onClick={() => setBookingStep('review')}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-md font-medium transition-colors">
              {hasAnyServices ? 'Continue to Review' : 'Skip & Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- REVIEW & CONFIRM STEP ----
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white mb-10">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-medium text-gray-900">Review & Confirm Booking</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
          </div>

          {bookingError && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {bookingError}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Flight Details */}
            <div className="lg:col-span-2">
              <h4 className="text-lg font-medium mb-4">Flight Information</h4>

              {allItineraries.map((itinerary, itinIdx) => (
                <div key={itinIdx}>
                  {isRoundTrip && (
                    <div className={`flex items-center gap-2 mb-2 ${itinIdx > 0 ? 'mt-6' : ''}`}>
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${itinIdx === 0 ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {itinIdx === 0 ? 'Outbound' : 'Return'}
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(itinerary.segments[0].departure.at)}
                      </span>
                    </div>
                  )}
                  {itinerary.segments.map((segment, segIdx) => (
                    <div key={segIdx} className="mb-4 p-4 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-medium text-lg">
                            {segment.departure.iataCode} &rarr; {segment.arrival.iataCode}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <AirlineLogo code={segment.carrierCode} segment={segment} size="sm" />
                            <span className="text-sm text-gray-500">{segment.carrierCode} {segment.number}</span>
                          </div>
                        </div>
                        <div className="text-right text-sm text-gray-600">
                          Segment {segIdx + 1} of {itinerary.segments.length}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm font-medium text-gray-700">Departure</div>
                          <div className="text-lg font-bold">{formatTime(segment.departure.at)}</div>
                          <div className="text-sm text-gray-600">{formatDate(segment.departure.at)}</div>
                          <div className="text-sm text-gray-500">
                            {segment.departure.iataCode}
                            {segment.departure.terminal && ` Terminal ${segment.departure.terminal}`}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-700">Arrival</div>
                          <div className="text-lg font-bold">{formatTime(segment.arrival.at)}</div>
                          <div className="text-sm text-gray-600">{formatDate(segment.arrival.at)}</div>
                          <div className="text-sm text-gray-500">
                            {segment.arrival.iataCode}
                            {segment.arrival.terminal && ` Terminal ${segment.arrival.terminal}`}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600">
                        <div className="grid grid-cols-2 gap-4">
                          <div>Aircraft: {segment.aircraft?.code || 'N/A'}</div>
                          <div>Duration: {formatDurationInline(segment.duration)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Passenger summary */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h5 className="font-medium mb-3">Passengers ({passengers.length})</h5>
                {passengers.map((pax, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                    <div>
                      <span className="font-medium">{pax.title.charAt(0).toUpperCase() + pax.title.slice(1)} {pax.given_name} {pax.family_name}</span>
                      {pax.email && <span className="ml-2 text-sm text-gray-500">{pax.email}</span>}
                    </div>
                    <button onClick={() => setBookingStep('passengers')} className="text-blue-600 text-sm hover:text-blue-800">
                      Edit
                    </button>
                  </div>
                ))}
              </div>

              {/* Terms and Conditions */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h5 className="font-medium mb-2">Important Information</h5>
                <div className="text-sm text-gray-600 space-y-2">
                  <p>&#8226; Prices are subject to availability and may change.</p>
                  <p>&#8226; Cancellation and change policies vary by airline and fare type.</p>
                  <p>&#8226; Additional fees may apply for seat selection, baggage, and other services.</p>
                </div>
                <div className="mt-3 flex items-center space-x-4 text-sm">
                  <button onClick={() => onShowTerms('terms', primaryCarrier)} className="text-blue-600 hover:text-blue-800 font-medium">Contract of Carriage</button>
                  <button onClick={() => onShowTerms('baggage', primaryCarrier)} className="text-blue-600 hover:text-blue-800 font-medium">Baggage Policy</button>
                  <button onClick={() => onShowTerms('cancellation', primaryCarrier)} className="text-blue-600 hover:text-blue-800 font-medium">Cancellation Policy</button>
                </div>
              </div>
            </div>

            {/* Booking Summary */}
            <div className="lg:col-span-1">
              <div>
                <h4 className="text-lg font-medium mb-4">Booking Summary</h4>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="space-y-3">
                    {/* Airfare — paid directly to the airline */}
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Paid to airline (credit card)</div>
                    <div className="flex justify-between">
                      <span>Base fare</span>
                      <span>{currencySymbol}{price.base}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Taxes & fees</span>
                      <span>{currencySymbol}{(parseFloat(price.total) - parseFloat(price.base)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Passengers</span>
                      <span>{passengers.length}</span>
                    </div>
                    {extrasCost > 0 && (
                      <div className="flex justify-between">
                        <span>Extras ({selectedServices.length} item{selectedServices.length > 1 ? 's' : ''})</span>
                        <span>{currencySymbol}{extrasCost.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t pt-3 flex justify-between font-bold">
                      <span>Airfare total</span>
                      <span className="text-blue-600">{currencySymbol}{(parseFloat(price.total) + extrasCost).toFixed(2)}</span>
                    </div>

                    {/* Booking fee — added to monthly invoice */}
                    {showMarkupLine && (
                      <>
                        <div className="border-t pt-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Added to monthly invoice</div>
                        <div className="flex justify-between text-sm">
                          <span>{markupLabel}</span>
                          <span className="text-amber-600">{currencySymbol}{totalMarkup.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <h5 className="font-medium mb-2">What's included</h5>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <span className="text-green-600 mr-2">&#10003;</span>
                      Personal item
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-600 mr-2">&#10003;</span>
                      {flight.travelerPricings?.[0]?.fareDetailsBySegment?.[0]?.includedCabinBags?.quantity || 1} carry-on bag
                    </div>
                    {selectedServices.length > 0 && availableServices ? (
                      selectedServices.map(sel => {
                        const allSvcs = [...(availableServices.baggage || []), ...(availableServices.seats || []), ...(availableServices.other || [])];
                        const svc = allSvcs.find(s => s.service_id === sel.id);
                        if (!svc) return null;
                        const weightInfo = svc.metadata?.maximum_weight_kg ? `${svc.metadata.maximum_weight_kg}kg` : '';
                        const bagType = svc.type === 'baggage' ? (svc.metadata?.type === 'checked' ? 'Checked bag' : 'Bag') : svc.type;
                        return (
                          <div key={sel.id} className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="text-green-600 mr-2">&#10003;</span>
                              {bagType} {weightInfo && `(${weightInfo})`} {sel.quantity > 1 ? `x${sel.quantity}` : ''}
                            </div>
                            <span className="text-gray-500">{currencySymbol}{(parseFloat(svc.total_amount || 0) * (sel.quantity || 1)).toFixed(2)}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex items-center">
                        <span className="text-orange-500 mr-2">+</span>
                        Checked bags (extra fee)
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Method — flights use card (client pays airline directly) */}
                <div className="mb-4">
                  <h5 className="font-medium mb-2 text-sm">Payment Method</h5>
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border rounded-lg border-blue-500 bg-blue-50">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={true}
                        readOnly
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium text-sm">Credit Card</div>
                        <div className="text-xs text-gray-500">Pay the airline directly with a credit or debit card</div>
                      </div>
                    </label>
                  </div>

                  {/* Saved cards for flights */}
                  {savedCards.length > 0 && !paymentIntentClientToken && (
                    <div className="mt-3">
                      <SavedCardsSelector
                        cards={savedCards}
                        selectedCardId={useNewCard ? null : selectedSavedCardId}
                        onSelect={(id) => { setSelectedSavedCardId(id); setUseNewCard(false); }}
                        onAddNew={() => { setUseNewCard(true); setSelectedSavedCardId(null); }}
                      />
                    </div>
                  )}

                  {totalMarkup > 0 && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                      Booking fee of {currencySymbol}{totalMarkup.toFixed(2)} will be added to your monthly invoice.
                    </div>
                  )}
                </div>

                {/* Terms acceptance */}
                <div className="mb-4 text-xs text-gray-500">
                  <label className="flex items-start cursor-pointer">
                    <input type="checkbox" className="mt-1 mr-2" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} />
                    <span>
                      I agree to the{' '}
                      <button onClick={() => onShowTerms('terms', primaryCarrier)} className="text-blue-600 hover:text-blue-800">Contract of Carriage</button>
                      {' '}and{' '}
                      <button onClick={() => onShowTerms('baggage', primaryCarrier)} className="text-blue-600 hover:text-blue-800">Baggage Policy</button>
                    </span>
                  </label>
                </div>

                <div className="space-y-3">
                  {(paymentMethod === 'balance' || paymentMethod === 'credit_card' || paymentMethod === 'ach') && (
                    <button
                      onClick={handleBookFlight}
                      disabled={bookingLoading || !termsAccepted}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-4 rounded-md font-medium text-lg transition-colors"
                    >
                      {bookingLoading ? 'Processing...' : `Confirm & Book - ${currencySymbol}${(parseFloat(price.total) + extrasCost + totalMarkup).toFixed(2)}`}
                    </button>
                  )}
                  {paymentMethod === 'card' && !paymentIntentClientToken && (
                    <button
                      onClick={handleSelectCardPayment}
                      disabled={bookingLoading || !termsAccepted}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-4 rounded-md font-medium text-lg transition-colors"
                    >
                      {bookingLoading ? 'Initializing card payment...' : `Proceed to Card Payment - ${currencySymbol}${(parseFloat(price.total) + extrasCost + totalMarkup).toFixed(2)}`}
                    </button>
                  )}
                  <button
                    onClick={() => setBookingStep('extras')}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-md font-medium transition-colors"
                  >
                    Back to Extras
                  </button>
                </div>

                <div className="mt-4 text-xs text-gray-500 text-center">
                  {flight.numberOfBookableSeats} seats remaining at this price
                </div>
              </div>
            </div>
          </div>

          {/* Card payment form - rendered full-width below the grid so it has enough space */}
          {paymentMethod === 'card' && paymentIntentClientToken && (
            <div className="mt-6">
              <h5 className="font-medium mb-3">Enter Card Details</h5>
              <DuffelPaymentsElement
                clientToken={paymentIntentClientToken}
                onSuccess={handleCardPaymentSuccess}
                onError={(err) => setBookingError(`Card payment failed: ${err?.message || 'Unknown error'}`)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Shared formatters
const fmtDuration = (duration) => {
  if (!duration) return 'N/A';
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return duration;
  const hours = match[1] ? match[1] + 'h ' : '';
  const minutes = match[2] ? match[2] + 'm' : '';
  return (hours + minutes).trim() || 'N/A';
};
const fmtTime = (dateTime) => new Date(dateTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
const fmtDate = (dateTime) => new Date(dateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtDateLong = (dateTime) => new Date(dateTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

// Helper to parse duration to minutes for sorting
const durationToMinutes = (duration) => {
  if (!duration) return Infinity;
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return Infinity;
  return (parseInt(match[1] || 0) * 60) + parseInt(match[2] || 0);
};

// Renders a single flight leg (one itinerary: outbound or return)
const FlightLeg = ({ itinerary, label }) => {
  const segments = itinerary.segments;
  const firstSeg = segments[0];
  const lastSeg = segments[segments.length - 1];
  const isNonstop = segments.length === 1;
  return (
    <div>
      {label && (
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          {label} &middot; {fmtDateLong(firstSeg.departure.at)}
        </div>
      )}
      <div className="flex items-center space-x-8">
        {/* Departure */}
        <div className="text-center min-w-[60px]">
          <div className="text-2xl font-bold text-gray-900">{fmtTime(firstSeg.departure.at)}</div>
          <div className="text-sm text-gray-500">{firstSeg.departure.iataCode}</div>
          {!label && <div className="text-xs text-gray-400">{fmtDate(firstSeg.departure.at)}</div>}
        </div>

        {/* Route line */}
        <div className="flex-1 text-center">
          <div className="text-sm text-gray-600 mb-1">{fmtDuration(itinerary.duration)}</div>
          <div className="relative">
            <div className="border-t-2 border-gray-300 relative">
              <div className="absolute left-0 top-0 w-2 h-2 bg-gray-400 rounded-full transform -translate-y-1"></div>
              <div className="absolute right-0 top-0 w-2 h-2 bg-gray-400 rounded-full transform -translate-y-1"></div>
              {!isNonstop && (
                <div className="absolute left-1/2 top-0 w-2 h-2 bg-orange-400 rounded-full transform -translate-x-1/2 -translate-y-1"></div>
              )}
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {isNonstop ? 'Nonstop' : `${segments.length - 1} stop${segments.length > 2 ? 's' : ''}`}
          </div>
          <div className="flex items-center justify-center mt-1 space-x-2">
            <AirlineLogo code={firstSeg.carrierCode} segment={firstSeg} size="sm" />
            <span className="text-xs text-gray-400">{firstSeg.carrierCode}{firstSeg.number}</span>
          </div>
          {!isNonstop && (
            <div className="text-xs text-gray-400 mt-1">
              via {segments.slice(0, -1).map(s => s.arrival.iataCode).join(', ')}
            </div>
          )}
        </div>

        {/* Arrival */}
        <div className="text-center min-w-[60px]">
          <div className="text-2xl font-bold text-gray-900">{fmtTime(lastSeg.arrival.at)}</div>
          <div className="text-sm text-gray-500">{lastSeg.arrival.iataCode}</div>
          {!label && <div className="text-xs text-gray-400">{fmtDate(lastSeg.arrival.at)}</div>}
        </div>
      </div>
    </div>
  );
};

// Flight Results Component with filters and sort
const FlightResults = ({ results, onSelectFlight }) => {
  const [sortBy, setSortBy] = useState('cheapest');
  const [airlineFilter, setAirlineFilter] = useState(new Set());
  const [stopsFilter, setStopsFilter] = useState('all'); // 'all', '0', '1', '2+'
  const [departureFilter, setDepartureFilter] = useState('all'); // 'all', 'morning', 'afternoon', 'evening', 'night'
  const [showFilters, setShowFilters] = useState(true);

  if (!results.data || !results.data.length) {
    return (
      <div className="text-center py-8">
        <span className="text-4xl">&#9992;&#65039;</span>
        <p className="mt-2 text-gray-500">No flights found</p>
        <p className="text-sm text-gray-400">Try adjusting your search criteria</p>
      </div>
    );
  }

  // Extract unique airlines with their info from all results
  const airlineMap = {};
  results.data.forEach(f => f.itineraries.forEach(it => it.segments.forEach(s => {
    if (!airlineMap[s.carrierCode]) airlineMap[s.carrierCode] = s;
  })));
  const allAirlines = Object.keys(airlineMap).sort();

  const toggleAirline = (code) => {
    setAirlineFilter(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  // Filter flights
  let filtered = results.data.filter(flight => {
    const outbound = flight.itineraries[0];
    const outSegs = outbound.segments;

    // Airline filter
    if (airlineFilter.size > 0) {
      const flightAirlines = flight.itineraries.flatMap(it => it.segments.map(s => s.carrierCode));
      if (!flightAirlines.some(a => airlineFilter.has(a))) return false;
    }

    // Stops filter (based on outbound)
    if (stopsFilter !== 'all') {
      const stops = outSegs.length - 1;
      if (stopsFilter === '0' && stops !== 0) return false;
      if (stopsFilter === '1' && stops !== 1) return false;
      if (stopsFilter === '2+' && stops < 2) return false;
    }

    // Departure time filter (outbound first segment)
    if (departureFilter !== 'all') {
      const hour = new Date(outSegs[0].departure.at).getHours();
      if (departureFilter === 'morning' && (hour < 6 || hour >= 12)) return false;
      if (departureFilter === 'afternoon' && (hour < 12 || hour >= 18)) return false;
      if (departureFilter === 'evening' && (hour < 18 || hour >= 24)) return false;
      if (departureFilter === 'night' && hour >= 6) return false;
    }

    return true;
  });

  // Sort
  filtered = [...filtered].sort((a, b) => {
    if (sortBy === 'cheapest') return parseFloat(a.price.total) - parseFloat(b.price.total);
    if (sortBy === 'fastest') return durationToMinutes(a.itineraries[0].duration) - durationToMinutes(b.itineraries[0].duration);
    if (sortBy === 'earliest') return new Date(a.itineraries[0].segments[0].departure.at) - new Date(b.itineraries[0].segments[0].departure.at);
    if (sortBy === 'latest') return new Date(b.itineraries[0].segments[0].departure.at) - new Date(a.itineraries[0].segments[0].departure.at);
    return 0;
  });

  const hasActiveFilters = airlineFilter.size > 0 || stopsFilter !== 'all' || departureFilter !== 'all';
  const clearFilters = () => { setAirlineFilter(new Set()); setStopsFilter('all'); setDepartureFilter('all'); };

  const isRoundTrip = results.data.length > 0 && results.data[0].itineraries.length > 1;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {hasActiveFilters ? `${filtered.length} of ${results.data.length}` : results.data.length} flight{results.data.length !== 1 ? 's' : ''} found
          {isRoundTrip && <span className="ml-1 text-blue-600 font-medium">(Round-trip)</span>}
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-700"
          >
            <option value="cheapest">Cheapest first</option>
            <option value="fastest">Fastest first</option>
            <option value="earliest">Earliest departure</option>
            <option value="latest">Latest departure</option>
          </select>
          <button
            onClick={() => setShowFilters(prev => !prev)}
            className={`text-sm font-medium px-3 py-1 rounded-md transition-colors ${showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Filters {hasActiveFilters && `(${[airlineFilter.size > 0, stopsFilter !== 'all', departureFilter !== 'all'].filter(Boolean).length})`}
          </button>
        </div>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          {/* Airlines */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Airlines</div>
            <div className="flex flex-wrap gap-2">
              {allAirlines.map(code => {
                const seg = airlineMap[code];
                const info = getAirlineInfo(code, seg);
                const active = airlineFilter.has(code);
                return (
                  <button key={code} onClick={() => toggleAirline(code)}
                    className={`flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                  >
                    <AirlineLogo code={code} segment={seg} size="sm" showName={false} />
                    <span>{info.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stops */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Stops</div>
            <div className="flex gap-2">
              {[['all', 'Any'], ['0', 'Nonstop'], ['1', '1 stop'], ['2+', '2+ stops']].map(([val, label]) => (
                <button key={val} onClick={() => setStopsFilter(val)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${stopsFilter === val ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                >{label}</button>
              ))}
            </div>
          </div>

          {/* Departure time */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Departure Time</div>
            <div className="flex gap-2">
              {[['all', 'Any'], ['morning', 'Morning 6-12'], ['afternoon', 'Afternoon 12-18'], ['evening', 'Evening 18-24'], ['night', 'Night 0-6']].map(([val, label]) => (
                <button key={val} onClick={() => setDepartureFilter(val)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${departureFilter === val ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                >{label}</button>
              ))}
            </div>
          </div>

          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No flights match your filters.</p>
          <button onClick={clearFilters} className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium">Clear filters</button>
        </div>
      ) : (
        filtered.map((flight, index) => (
          <FlightCard
            key={flight.id || index}
            flight={flight}
            onSelect={() => onSelectFlight(flight)}
          />
        ))
      )}
    </div>
  );
};

// Individual Flight Card Component — supports one-way and round-trip
const FlightCard = ({ flight, onSelect }) => {
  const price = flight.price;
  const travelerPricing = flight.travelerPricings[0];
  const isRoundTrip = flight.itineraries.length > 1;
  const currSym = price.currency === 'USD' ? '$' : price.currency === 'EUR' ? '\u20AC' : price.currency === 'GBP' ? '\u00A3' : price.currency + ' ';
  const conditions = flight.conditions || {};
  const refundable = conditions.refund_before_departure?.allowed;
  const changeable = conditions.change_before_departure?.allowed;

  return (
    <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        {/* Left side - Flight legs */}
        <div className="flex-1 space-y-4">
          {flight.itineraries.map((itin, idx) => (
            <FlightLeg
              key={idx}
              itinerary={itin}
              label={isRoundTrip ? (idx === 0 ? 'Outbound' : 'Return') : null}
            />
          ))}

          {/* Additional flight details */}
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
            <div>
              <span className="font-medium">Class:</span> {travelerPricing.fareDetailsBySegment[0]?.cabin || 'Economy'}
            </div>
            {travelerPricing.fareDetailsBySegment[0]?.brandedFare && (
              <div>
                <span className="font-medium">Fare:</span> {travelerPricing.fareDetailsBySegment[0].brandedFare}
              </div>
            )}
            {/* Fare flexibility badges */}
            {refundable !== undefined && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${refundable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {refundable ? (
                  <>{'\u2713'} Refundable{conditions.refund_before_departure?.penalty_amount ? ` (${currSym}${conditions.refund_before_departure.penalty_amount} fee)` : ''}</>
                ) : 'Non-refundable'}
              </span>
            )}
            {changeable !== undefined && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${changeable ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                {changeable ? (
                  <>{'\u2713'} Changeable{conditions.change_before_departure?.penalty_amount ? ` (${currSym}${conditions.change_before_departure.penalty_amount} fee)` : ''}</>
                ) : 'Non-changeable'}
              </span>
            )}
          </div>
        </div>

        {/* Right side - Price and booking */}
        <div className="text-right ml-6 flex-shrink-0">
          <div className="text-3xl font-bold text-blue-600">
            {currSym}{price.total}
          </div>
          <div className="text-sm text-gray-500 mb-1">
            {isRoundTrip ? 'round-trip' : 'per person'}
          </div>

          <button
            onClick={onSelect}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
          >
            Select
          </button>

          <div className="mt-2 text-xs text-gray-400">
            {flight.numberOfBookableSeats} seats left
          </div>
        </div>
      </div>

      {/* Baggage */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <div className="flex items-center">
            <span className="text-green-600 mr-1">&#10003;</span>
            Personal item
          </div>
          <div className="flex items-center">
            <span className="text-green-600 mr-1">&#10003;</span>
            {travelerPricing.fareDetailsBySegment[0]?.includedCabinBags?.quantity || 1} carry-on
          </div>
          <div className="flex items-center">
            <span className="text-orange-500 mr-1">+</span>
            Checked bag (extra)
          </div>
        </div>
      </div>
    </div>
  );
};

// Terms Modal Component - Airline-Specific Contract of Carriage
const TermsModal = ({ type, airlineCode, onClose }) => {
  const getAirlineInfo = (code) => {
    const airlines = {
      'UA': 'United Airlines',
      'AA': 'American Airlines', 
      'DL': 'Delta Air Lines',
      'AS': 'Alaska Airlines',
      'B6': 'JetBlue Airways',
      'F9': 'Frontier Airlines',
      'NK': 'Spirit Airlines',
      'WN': 'Southwest Airlines',
      'LH': 'Lufthansa',
      'BA': 'British Airways',
      'AF': 'Air France',
      'KL': 'KLM',
      'EK': 'Emirates',
      'QR': 'Qatar Airways'
    };
    return airlines[code] || code;
  };

  const getContent = () => {
    const airlineName = getAirlineInfo(airlineCode);
    
    switch (type) {
      case 'terms':
        return {
          title: `${airlineName} - Contract of Carriage`,
          content: getContractOfCarriage(airlineCode, airlineName)
        };
      case 'baggage':
        return {
          title: `${airlineName} - Baggage Policy`,
          content: getBaggagePolicy(airlineCode, airlineName)
        };
      case 'cancellation':
        return {
          title: `${airlineName} - Cancellation & Change Policy`,
          content: getCancellationPolicy(airlineCode, airlineName)
        };
      default:
        return { title: 'Information', content: 'Content not available.' };
    }
  };

  const getContractOfCarriage = (code, name) => {
    const commonTerms = `
**${name} Contract of Carriage**

This contract governs your transportation and the services provided by ${name}.

**1. DEFINITIONS AND INTERPRETATION**
• "Carrier" refers to ${name} and its authorized agents
• "Passenger" refers to any person traveling on a ticket issued by the Carrier
• "Ticket" means the document issued by the Carrier as evidence of the contract

**2. APPLICABILITY**
This contract applies to all flights operated by ${name} (${code}) and codeshare partners.

**3. TICKETS**
• Tickets are non-transferable and valid only for the person named
• Tickets must be used in sequence as issued
• Changes may require additional payment

**4. FARES AND CHARGES**
• All fares are subject to change until payment is made
• Additional fees may apply for services, baggage, and seat selection
• Taxes and fees are subject to change

**5. RESERVATIONS AND CHECK-IN**
• Reservations must be confirmed and tickets must be issued
• Check-in deadlines must be met to avoid cancellation
• Advance seat selection may incur additional charges

**6. REFUSAL TO TRANSPORT**
The Carrier may refuse transport for safety, security, or operational reasons.

**7. BAGGAGE**
• Baggage allowances vary by fare type and destination
• Excess baggage fees apply beyond allowances
• Restricted items are prohibited

**8. SCHEDULES AND DELAYS**
• Flight schedules are not guaranteed
• The Carrier will make reasonable efforts to maintain schedules
• Compensation for delays varies by jurisdiction

**9. LIABILITY**
• Liability is limited under international conventions
• Additional protection may be available through travel insurance
• Claims must be filed within specified time limits
    `;

    // Add airline-specific terms
    const specificTerms = {
      'UA': `
**UNITED AIRLINES SPECIFIC TERMS**
• MileagePlus members receive priority benefits
• Basic Economy restrictions apply to lowest fares
• Premier members receive upgraded services
• 24-hour flexible booking policy available
      `,
      'AA': `
**AMERICAN AIRLINES SPECIFIC TERMS**
• AAdvantage program benefits apply
• Basic Economy has restricted change/cancel policies
• Main Cabin Extra provides additional legroom
• Same-day flight changes available for fee
      `,
      'DL': `
**DELTA AIR LINES SPECIFIC TERMS**
• SkyMiles program benefits included
• Basic Economy restrictions on changes/upgrades
• Delta Comfort+ offers premium experience
• 24-hour risk-free cancellation available
      `,
      'AS': `
**ALASKA AIRLINES SPECIFIC TERMS**
• Mileage Plan benefits apply to all bookings
• Saver fares have restrictions on changes
• Free entertainment on most flights
• Same-day standby available for fee
      `,
      'WN': `
**SOUTHWEST AIRLINES SPECIFIC TERMS**
• Two free checked bags included
• No change fees on most fares
• Open seating policy - no assigned seats
• Rapid Rewards points never expire
      `
    };

    return commonTerms + (specificTerms[code] || '') + `

**IMPORTANT NOTICES**
• This is a summary of key terms - full contract available at ${name} website
• Terms subject to applicable laws and international conventions
• By purchasing this ticket, you agree to these terms and conditions

For complete terms, visit: www.${name.toLowerCase().replace(/\s+/g, '')}.com/legal
Last updated: August 2025
    `;
  };

  const getBaggagePolicy = (code, name) => {
    const policies = {
      'UA': `
**UNITED AIRLINES BAGGAGE POLICY**

**CARRY-ON BAGGAGE**
• Personal item: FREE (under seat)
• Carry-on bag: FREE on most fares (overhead bin)
• Size: 22" x 14" x 9" including handles and wheels
• Weight: No limit

**CHECKED BAGGAGE**
• First bag: $35 domestic, varies internationally
• Second bag: $45 domestic, varies internationally  
• Size: 62 linear inches maximum
• Weight: 50 lbs maximum (50 lbs = $100-200 fee)

**BASIC ECONOMY**
• Personal item only included
• Carry-on bag: $35 fee
• No free checked bags

**RESTRICTED ITEMS**
• Liquids over 3.4 oz in carry-on
• Sharp objects and tools
• Firearms (special procedures required)
• See TSA guidelines for complete list
      `,
      'AA': `
**AMERICAN AIRLINES BAGGAGE POLICY**

**CARRY-ON BAGGAGE**
• Personal item: FREE
• Carry-on: FREE (except Basic Economy)
• Size: 22" x 14" x 9"
• Weight: No restriction

**CHECKED BAGGAGE**
• First bag: $30 domestic, varies international
• Second bag: $40 domestic
• 50 lbs weight limit
• 62 linear inches size limit

**BASIC ECONOMY**
• Only personal item included
• Carry-on: $35 fee
• Checked bags: Standard fees apply
      `,
      'DL': `
**DELTA AIR LINES BAGGAGE POLICY**

**CARRY-ON BAGGAGE**
• Personal item: FREE
• Carry-on: FREE (most fares)
• Size: 22" x 14" x 9"

**CHECKED BAGGAGE**
• First bag: $30 domestic
• Second bag: $40 domestic
• 50 lb weight limit
• 62 linear inches maximum

**BASIC ECONOMY**
• Personal item only
• Carry-on: $35
• No free checked bags
      `,
      'WN': `
**SOUTHWEST AIRLINES BAGGAGE POLICY**

**CARRY-ON BAGGAGE**
• Personal item: FREE
• Carry-on bag: FREE
• Size: 24" x 16" x 10"

**CHECKED BAGGAGE**
• First bag: FREE! 
• Second bag: FREE!
• 50 lb weight limit
• 62 linear inches maximum

**SOUTHWEST ADVANTAGE**
• Two free checked bags on all fares
• No change fees
• Bags fly free policy
      `
    };

    return policies[code] || `
**${name} BAGGAGE POLICY**

**CARRY-ON BAGGAGE**
• Personal item typically included
• Carry-on allowance varies by fare type
• Size restrictions apply

**CHECKED BAGGAGE**
• Fees vary by route and fare type
• Weight and size limits apply
• Additional fees for overweight/oversized bags

For specific details, please visit ${name} official website.
    `;
  };

  const getCancellationPolicy = (code, name) => {
    return `
**${name} CANCELLATION & CHANGE POLICY**

**24-HOUR FLEXIBLE BOOKING**
• FREE cancellation within 24 hours of booking
• Applies to flights departing 7+ days later
• Full refund to original payment method

**CHANGE FEES**
${code === 'WN' ? '• NO CHANGE FEES on most Southwest fares' : '• Change fees vary by fare type and route'}
${code === 'UA' || code === 'AA' || code === 'DL' ? '• Basic Economy: Changes not permitted' : ''}
• Same-day changes may be available for fee

**CANCELLATION REFUNDS**
• Refundable fares: Full refund minus any penalties
• Non-refundable fares: May receive credit for future travel
• Basic Economy: Generally non-refundable

**SCHEDULE CHANGES**
• Significant schedule changes allow free rebooking
• Minor changes (under 90 minutes) no compensation
• Major delays may qualify for meal/hotel vouchers

**WEATHER/IRREGULAR OPERATIONS**
• No compensation for weather-related cancellations
• Rebooking on next available flight at no charge
• Hotel/meal vouchers at airline discretion

**VOLUNTARY CHANGES**
• Change fees apply (varies by fare type)
• Fare difference may be required
• Some premium fares allow free changes

**IMPORTANT NOTES**
• Policies vary significantly by fare type
• International flights may have different rules
• Travel insurance recommended for flexibility
• Credits typically expire 12 months from issue

For complete policy details, visit: ${name} official website
Last updated: August 2025
    `;
  };

  const { title, content } = getContent();

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-xl font-medium text-gray-900">{title}</h3>
              <p className="text-sm text-gray-500 mt-1">
                Specific to your {getAirlineInfo(airlineCode)} booking
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            <div className="prose prose-sm max-w-none">
              {content.split('\n').map((line, index) => {
                if (line.startsWith('**') && line.endsWith('**')) {
                  return <h4 key={index} className="font-bold text-gray-900 mt-4 mb-2">{line.slice(2, -2)}</h4>;
                }
                if (line.startsWith('•')) {
                  return <p key={index} className="ml-4 mb-1 text-gray-700">{line}</p>;
                }
                if (line.trim() === '') {
                  return <div key={index} className="mb-2"></div>;
                }
                return <p key={index} className="mb-2 text-gray-700">{line}</p>;
              })}
            </div>
          </div>
          
          <div className="mt-6 flex justify-between items-center">
            <p className="text-xs text-gray-500">
              This information is specific to {getAirlineInfo(airlineCode)} flights. 
              For complete and current terms, visit the airline's official website.
            </p>
            <button 
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hotel Results Component
const HotelResults = ({ results, searchParams, onSelectHotel }) => {
  if (!results.data || !results.data.length) {
    return (
      <div className="text-center py-8">
        <span className="text-4xl">🏨</span>
        <p className="mt-2 text-gray-500">No hotels found</p>
        <p className="text-sm text-gray-400">Try adjusting your search criteria</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {results.data.length} hotels found
        </div>
        <div className="flex items-center space-x-4 text-sm text-gray-500">
          <div className="flex items-center">
            <span className="w-2 h-2 bg-blue-400 rounded-full mr-1"></span>
            Total stay price
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-1"></span>
            Includes taxes & fees
          </div>
        </div>
      </div>
      
      {results.data.map((hotel, index) => (
        <HotelCard
          key={hotel.hotel?.hotelId || index}
          hotel={hotel}
          searchParams={searchParams}
          onSelect={() => onSelectHotel(hotel)}
        />
      ))}
    </div>
  );
};

// Individual Hotel Card Component
const HotelCard = ({ hotel, searchParams, onSelect }) => {
  const [hotelImage, setHotelImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);

  const hotelInfo = hotel.hotel;
  const offers = hotel.offers || [];
  const bestOffer = offers.length > 0 ? offers[0] : null;

  if (!hotelInfo || !bestOffer) {
    return null;
  }

  // Use Duffel photos if available, otherwise fall back to Google Places
  useEffect(() => {
    const duffelPhotos = hotelInfo.photos || [];
    if (duffelPhotos.length > 0) {
      setHotelImage(duffelPhotos[0]);
      setImageLoading(false);
      return;
    }

    const fetchHotelImageFromGooglePlaces = async () => {
      try {
        if (window.google && window.google.maps && window.google.maps.places) {
          const service = new window.google.maps.places.PlacesService(document.createElement('div'));

          const request = {
            query: `${hotelInfo.name} ${hotelInfo.address?.cityName || ''}`,
            fields: ['photos', 'name', 'place_id']
          };

          service.textSearch(request, (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results[0]) {
              const place = results[0];
              if (place.photos && place.photos.length > 0) {
                const photoUrl = place.photos[0].getUrl({
                  maxWidth: 300,
                  maxHeight: 200
                });
                setHotelImage(photoUrl);
              } else {
                setHotelImage(getFallbackImage());
              }
            } else {
              setHotelImage(getFallbackImage());
            }
            setImageLoading(false);
          });
        } else {
          setHotelImage(getFallbackImage());
          setImageLoading(false);
        }
      } catch (error) {
        console.error('Error fetching hotel image from Google Places:', error);
        setHotelImage(getFallbackImage());
        setImageLoading(false);
      }
    };

    if (window.google && window.google.maps && window.google.maps.places) {
      fetchHotelImageFromGooglePlaces();
    } else {
      const checkGooglePlaces = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkGooglePlaces);
          fetchHotelImageFromGooglePlaces();
        }
      }, 500);

      setTimeout(() => {
        clearInterval(checkGooglePlaces);
        if (!hotelImage) {
          setHotelImage(getFallbackImage());
          setImageLoading(false);
        }
      }, 10000);
    }
  }, [hotelInfo.name, hotelInfo.address?.cityName]);

  // Generate star rating display
  const getStarRating = (rating) => {
    if (!rating) return null;
    const stars = Math.floor(rating);
    return '★'.repeat(stars) + '☆'.repeat(5 - stars);
  };

  // Format price
  // price.total from Duffel is already the total stay amount (not per-night)
  const formatPrice = (price) => {
    return price?.total || 'N/A';
  };

  // price.total is already the total stay amount — no multiplication needed
  const calculateTotalPrice = (price) => {
    if (!price?.total) return 'N/A';
    return parseFloat(price.total).toFixed(2);
  };

  // Per-night price = total stay / number of nights
  const calculatePerNightPrice = (price, nights = 1) => {
    if (!price?.total) return 'N/A';
    return (parseFloat(price.total) / nights).toFixed(2);
  };

  // Get room type info
  const getRoomInfo = (offer) => {
    if (!offer.room) return 'Standard Room';
    return offer.room.typeEstimated?.category || offer.room.type?.description || 'Room';
  };

  // Get fallback image URL
  const getFallbackImage = () => {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTA1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOUI5QjlCIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiPkhvdGVsIEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
  };

  const guestRating = hotelInfo.reviewScore || null;
  const nights = (() => {
    if (!searchParams?.check_in_date || !searchParams?.check_out_date) return 1;
    const checkIn = new Date(searchParams.check_in_date);
    const checkOut = new Date(searchParams.check_out_date);
    const timeDiff = checkOut.getTime() - checkIn.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff > 0 ? daysDiff : 1;
  })();
  const currencySymbol = bestOffer.price?.currency === 'EUR' ? '€' : '$';

  const handleSelectClick = () => {
    onSelect();
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex">
        {/* Hotel Image */}
        <div className="w-64 h-48 flex-shrink-0">
          {imageLoading ? (
            <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
              <span className="text-gray-400">Loading image...</span>
            </div>
          ) : (
            <img 
              src={hotelImage || getFallbackImage()}
              alt={hotelInfo.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = getFallbackImage();
              }}
            />
          )}
        </div>

        {/* Hotel Information */}
        <div className="flex-1 p-6">
          <div className="flex justify-between">
            <div className="flex-1">
              {/* Hotel Name and Rating */}
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    {hotelInfo.name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    {hotelInfo.rating && (
                      <div className="flex items-center">
                        <span className="text-yellow-400 text-lg">
                          {getStarRating(hotelInfo.rating)}
                        </span>
                        <span className="text-sm text-gray-600 ml-1">
                          {hotelInfo.rating}-star hotel
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Guest Rating */}
                {guestRating && (
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Guest rating</div>
                      <div className="text-sm font-medium text-gray-500">
                        {guestRating >= 9 ? 'Excellent' : guestRating >= 8 ? 'Very Good' : guestRating >= 7 ? 'Good' : 'Fair'}
                        {hotelInfo.reviewCount ? ` (${hotelInfo.reviewCount})` : ''}
                      </div>
                    </div>
                    <div className="bg-blue-600 text-white text-sm font-bold px-2 py-1 rounded">
                      {Number(guestRating).toFixed(1)}
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Location */}
              {hotelInfo.address && (
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <span className="mr-1">📍</span>
                  <span>{hotelInfo.address.cityName}</span>
                  {hotelInfo.address.countryCode && (
                    <span>, {hotelInfo.address.countryCode}</span>
                  )}
                </div>
              )}

              {/* Room Type */}
              <div className="text-sm text-gray-700 mb-2">
                <span className="font-medium">Room:</span> {getRoomInfo(bestOffer)}
              </div>

              {/* Amenities/Features */}
              <div className="flex flex-wrap gap-2 mb-4">
                {(hotelInfo.amenities || []).slice(0, 4).map((amenity, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ {amenity}
                  </span>
                ))}
                {hotelInfo.brand && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {hotelInfo.brand}
                  </span>
                )}
              </div>

              {/* Cancellation Policy */}
              <div className="text-xs text-gray-600">
                {bestOffer.policies?.cancellation ? (
                  <span className="text-green-600">✓ Free cancellation</span>
                ) : (
                  <span className="text-orange-600">⚠ Non-refundable</span>
                )}
              </div>
            </div>

            {/* Price and Booking Section */}
            <div className="text-right ml-6 flex flex-col justify-between">
              <div>
                <div className="text-sm text-gray-500 mb-1">
                  {nights} night{nights > 1 ? 's' : ''} total
                </div>
                <div className="text-3xl font-bold text-blue-600 mb-1">
                  {currencySymbol}{calculateTotalPrice(bestOffer.price)}
                </div>
                <div className="text-sm text-gray-500 mb-2">
                  {currencySymbol}{calculatePerNightPrice(bestOffer.price, nights)} per night
                </div>
                <div className="text-xs text-gray-400 mb-4">
                  Includes taxes & fees
                </div>
              </div>

              <div>
                <button 
                  onClick={handleSelectClick}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors w-full mb-2"
                >
                  Select Room
                </button>
                
                <div className="text-xs text-gray-400">
                  {offers.length > 1 && `${offers.length - 1} more rate${offers.length > 2 ? 's' : ''} available`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional offer details */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4 text-gray-600">
            {(() => {
              const paymentTypes = [...new Set(offers.map(o => o.policies?.paymentType).filter(Boolean))];
              if (paymentTypes.length > 1) {
                return paymentTypes.map((pt, i) => (
                  <div key={i} className="flex items-center">
                    <span className={`mr-1 ${pt === 'Pay in advance' ? 'text-blue-600' : 'text-green-600'}`}>✓</span>
                    {pt}
                  </div>
                ));
              }
              return (
                <div className="flex items-center">
                  <span className="text-green-600 mr-1">✓</span>
                  {bestOffer.policies?.paymentType || 'Pay in advance'}
                </div>
              );
            })()}
            {bestOffer.policies?.cancellation && Object.keys(bestOffer.policies.cancellation).length > 0 && (
              <div className="flex items-center">
                <span className="text-green-600 mr-1">✓</span>
                Free cancellation until check-in
              </div>
            )}
          </div>

          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            View all {offers.length} rate{offers.length > 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hotel Booking Modal Component
const HotelBookingModal = ({ hotel, searchParams, markupConfig, hotelPaymentMethods, onClose }) => {
  const { labels } = useParadigm();
  const [bookingStep, setBookingStep] = useState('details'); // 'details', 'guest', 'confirm', 'success'
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingConfirmationNumber, setBookingConfirmationNumber] = useState(null);
  const [selectedOfferIndex, setSelectedOfferIndex] = useState(0);

  // Full rooms/rates fetched from Duffel
  const [allRooms, setAllRooms] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState('');
  const [selectedRoomIndex, setSelectedRoomIndex] = useState(0);
  const [selectedRateIndex, setSelectedRateIndex] = useState(0);

  // Guest details state
  const [guest, setGuest] = useState({
    given_name: '',
    family_name: '',
    email: '',
    phone_number: '',
    _executive_id: '',
  });
  const [executives, setExecutives] = useState([]);
  const [executivesLoading, setExecutivesLoading] = useState(true);

  // Payment state
  const allowedMethods = hotelPaymentMethods || ['booker_card'];
  const [paymentMethod, setPaymentMethod] = useState(
    allowedMethods.includes('booker_card') ? 'booker_card' : (allowedMethods[0] || 'booker_card')
  );
  const [paymentIntentClientToken, setPaymentIntentClientToken] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedCardId, setSelectedSavedCardId] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [saveCardForFuture, setSaveCardForFuture] = useState(true);

  // Fetch saved cards on mount
  useEffect(() => {
    const fetchSavedCards = async () => {
      try {
        const response = await axios.get(`${getApiBase()}/saved-cards`, { headers: getAuthHeaders() });
        if (response.data.success) {
          setSavedCards(response.data.data || []);
          // Auto-select default card
          const defaultCard = (response.data.data || []).find(c => c.is_default);
          if (defaultCard) setSelectedSavedCardId(defaultCard.id);
        }
      } catch (err) {
        console.error('Failed to load saved cards:', err);
      }
    };
    fetchSavedCards();
  }, []);

  // Fetch booking-ready executives on mount
  useEffect(() => {
    const fetchExecs = async () => {
      try {
        const response = await axios.get(`${getApiBase()}/executives/booking-ready`, { headers: getAuthHeaders() });
        setExecutives(response.data.executives || []);
      } catch (err) {
        console.error('Failed to load executives:', err);
      } finally {
        setExecutivesLoading(false);
      }
    };
    fetchExecs();
  }, []);

  // Fetch all rooms and rates for the selected hotel
  useEffect(() => {
    const fetchAllRates = async () => {
      const searchResultId = hotel.duffel_stay_id;
      if (!searchResultId) return;
      setRatesLoading(true);
      setRatesError('');
      try {
        const response = await axios.post(
          `${getApiBase()}/stays/search_results/${searchResultId}/rates`,
          {},
          { headers: getAuthHeaders() }
        );
        if (response.data.success && response.data.data?.rooms?.length) {
          setAllRooms(response.data.data.rooms);
          setSelectedRoomIndex(0);
          setSelectedRateIndex(0);
        } else {
          setRatesError(response.data.error || 'No rooms available');
        }
      } catch (err) {
        console.error('Failed to fetch all rates:', err);
        setRatesError('Failed to load room options. Showing initial rates.');
      } finally {
        setRatesLoading(false);
      }
    };
    fetchAllRates();
  }, [hotel.duffel_stay_id]);

  const fillGuestFromExecutive = (executiveId) => {
    if (!executiveId) {
      setGuest({ given_name: '', family_name: '', email: '', phone_number: '', _executive_id: '' });
      return;
    }
    const exec = executives.find(e => e.id === executiveId);
    if (!exec) return;
    setGuest({
      given_name: exec.first_name || '',
      family_name: exec.last_name || '',
      email: exec.email || '',
      phone_number: exec.phone || '',
      _executive_id: executiveId,
    });
  };

  const updateGuest = (field, value) => {
    setGuest(prev => ({ ...prev, [field]: value }));
  };

  const isGuestFormValid = () => {
    return guest.given_name.trim() && guest.family_name.trim() && guest.email.trim();
  };

  const hotelInfo = hotel.hotel;
  const offers = hotel.offers || [];
  const bestOffer = offers.length > 0 ? offers[0] : null;

  // When full rates are loaded, build a selectedOffer from the chosen room/rate
  const selectedRoom = allRooms ? allRooms[selectedRoomIndex] : null;
  const selectedRate = selectedRoom?.rates?.[selectedRateIndex] || null;

  // Build an offer-compatible object from the fetched rate, or fall back to initial offers
  const selectedOffer = selectedRate
    ? {
        id: selectedRate.id,
        price: {
          total: selectedRate.total_amount,
          base: selectedRate.base_amount,
          currency: selectedRate.currency,
        },
        room: {
          description: { text: selectedRoom.name || selectedRoom.description },
          typeEstimated: { category: selectedRoom.name },
        },
        policies: {
          cancellation: selectedRate.cancellation,
          paymentType: selectedRate.payment_type_label,
          paymentTypeRaw: selectedRate.payment_type,
        },
        boardType: selectedRate.board_type,
      }
    : (offers[selectedOfferIndex] || bestOffer);

  if (!hotelInfo || (!bestOffer && !selectedRate)) {
    return null;
  }

  // Calculate number of nights
  const calculateNights = () => {
    if (!searchParams.check_in_date || !searchParams.check_out_date) return 1;
    const checkIn = new Date(searchParams.check_in_date);
    const checkOut = new Date(searchParams.check_out_date);
    const timeDiff = checkOut.getTime() - checkIn.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff > 0 ? daysDiff : 1;
  };

  const nights = calculateNights();
  // Both fetched rates (total_amount) and initial offers (price.total) are total stay amounts
  const basePrice = selectedRate
    ? parseFloat(selectedRate.total_amount || 0)
    : (selectedOffer.price?.total ? parseFloat(selectedOffer.price.total) : 0);
  const totalPrice = basePrice.toFixed(2);
  const currencySymbol = (selectedRate?.currency || selectedOffer.price?.currency) === 'EUR' ? '\u20AC' : '$';

  // Calculate hotel markup — mirrors backend _resolve_hotel_fee_fields logic
  const mc = markupConfig || {};
  const rawPt = selectedOffer.policies?.paymentTypeRaw || '';
  const offerIsPrepaid = ['prepaid', 'pay_now'].includes(rawPt) || !rawPt;

  // Generic markup calculator for any amount and payment type
  const calcMarkupForAmount = (amount, isPrepaidRate) => {
    if (!markupConfig) return 0;

    // 1. Per-category fields (hotel_prepaid_markup_* or hotel_pay_at_hotel_fee_*)
    let feeType, flat, pct, minFee, maxFee;
    if (isPrepaidRate && mc.hotel_prepaid_markup_type && mc.hotel_prepaid_markup_type !== 'none') {
      feeType = mc.hotel_prepaid_markup_type;
      flat = mc.hotel_prepaid_markup_flat || 0;
      pct = mc.hotel_prepaid_markup_pct || 0;
      minFee = mc.hotel_prepaid_markup_min || 0;
      maxFee = mc.hotel_prepaid_markup_max || 0;
    } else if (!isPrepaidRate && mc.hotel_pay_at_hotel_fee_type && mc.hotel_pay_at_hotel_fee_type !== 'none') {
      feeType = mc.hotel_pay_at_hotel_fee_type;
      flat = mc.hotel_pay_at_hotel_fee_flat || 0;
      pct = mc.hotel_pay_at_hotel_fee_pct || 0;
      minFee = mc.hotel_pay_at_hotel_fee_min || 0;
      maxFee = mc.hotel_pay_at_hotel_fee_max || 0;
    } else {
      // 2. Legacy fields
      feeType = mc.hotel_booking_fee_type || mc.hotel_markup_type || 'none';
      const legacyVal = mc.hotel_markup_value || 0;
      flat = mc.hotel_booking_fee_flat || (feeType === 'flat' ? legacyVal : 0);
      pct = mc.hotel_booking_fee_pct || (feeType === 'percentage' ? legacyVal : 0);
      minFee = mc.hotel_booking_fee_min || 0;
      maxFee = mc.hotel_booking_fee_max || 0;
    }

    if (feeType === 'none') return 0;
    let fee = 0;
    if (feeType === 'flat') fee = flat;
    else if (feeType === 'percentage') fee = amount * (pct / 100);
    else if (feeType === 'both') fee = flat + amount * (pct / 100);
    if (minFee > 0) fee = Math.max(fee, minFee);
    if (maxFee > 0) fee = Math.min(fee, maxFee);
    return Math.round(fee * 100) / 100;
  };

  const hotelMarkup = calcMarkupForAmount(basePrice, offerIsPrepaid);
  const customerTotal = (basePrice + hotelMarkup).toFixed(2);
  // For prepaid hotels, markup is rolled into the room price — never show it as a separate line.
  // Booking fees are only shown separately for pay-at-hotel rates.
  const showMarkupLine = !offerIsPrepaid && (mc.show_booking_fee_separately || mc.show_markup_as_separate_line) && hotelMarkup > 0;
  const markupLabel = mc.booking_fee_label || 'Booking Fee';
  // For prepaid, display the markup-inclusive price as the room price
  const displayRoomPrice = offerIsPrepaid ? customerTotal : totalPrice;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRoomInfo = (offer) => {
    if (!offer.room) return 'Standard Room';
    return offer.room.typeEstimated?.category || offer.room.type?.description || 'Room';
  };

  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  // Create Stripe PaymentIntent for booker card payment (prepaid hotels)
  const handleInitStripePayment = async () => {
    setBookingError('');
    setBookingLoading(true);
    try {
      const response = await axios.post(`${apiBase}/payments/stripe/create-booking-intent`, {
        amount: customerTotal,
        currency: (selectedOffer.price?.currency || 'USD').toLowerCase(),
        save_card: saveCardForFuture,
        saved_card_id: (!useNewCard && selectedSavedCardId) ? selectedSavedCardId : undefined,
        description: `Hotel: ${hotelInfo.name} (${searchParams.check_in_date} to ${searchParams.check_out_date})`,
      }, { headers });

      if (response.data.success) {
        setStripePaymentIntentId(response.data.payment_intent_id);
        if (response.data.status === 'succeeded') {
          // Saved card charged off-session — proceed to booking directly
          await completeHotelBooking(response.data.payment_intent_id);
        } else {
          // New card — show Stripe Elements form
          setStripeClientSecret(response.data.client_secret);
        }
      } else {
        setBookingError(response.data.error || 'Failed to initialize payment.');
      }
    } catch (err) {
      console.error('Hotel Stripe payment init error:', err);
      setBookingError('Failed to initialize payment. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  // Legacy: Create a Duffel payment intent for legacy card payments
  const handleSelectCardPayment = async () => {
    setBookingError('');
    setBookingLoading(true);
    try {
      const response = await axios.post(`${apiBase}/payments/duffel/create-intent`, {
        amount: customerTotal,
        currency: selectedOffer.price?.currency || 'USD',
      }, { headers });

      if (response.data.success && response.data.data.client_token) {
        setPaymentIntentClientToken(response.data.data.client_token);
      } else {
        setBookingError(response.data.error || 'Failed to initialize card payment.');
      }
    } catch (err) {
      console.error('Hotel payment intent error:', err);
      setBookingError('Failed to initialize card payment.');
    } finally {
      setBookingLoading(false);
    }
  };

  // Called when Stripe card payment succeeds
  const handleStripePaymentSuccess = useCallback(async (paymentIntent) => {
    // Save card if opted in
    if (saveCardForFuture && paymentIntent?.id) {
      try {
        await axios.post(`${apiBase}/payments/stripe/confirm-and-save`, {
          payment_intent_id: paymentIntent.id,
          save_card: true,
        }, { headers });
      } catch (err) {
        console.warn('Failed to save card for future use:', err);
      }
    }
    await completeHotelBooking(paymentIntent?.id || stripePaymentIntentId);
  }, [stripePaymentIntentId, saveCardForFuture]);

  // Legacy: Called by DuffelPayments component when card collection succeeds
  const handleCardPaymentSuccess = useCallback(async (metadata) => {
    setBookingLoading(true);
    setBookingError('');
    try {
      const hotelData = buildHotelBookingPayload();
      hotelData.payment_method = 'card';
      hotelData.payment_intent_id = metadata?.id || paymentIntentClientToken;

      const response = await axios.post(`${apiBase}/bookings/hotels`, hotelData, { headers });
      if (response.data.success) {
        setBookingConfirmationNumber(response.data.confirmation_number || null);
        setBookingStep('success');
      } else {
        setBookingError(response.data.error || 'Booking failed.');
      }
    } catch (error) {
      console.error('Hotel card booking error:', error);
      setBookingError(error.response?.data?.error || error.message || 'Unable to complete hotel booking.');
    } finally {
      setBookingLoading(false);
    }
  }, [paymentIntentClientToken, guest, selectedOffer, hotel, hotelInfo, searchParams]);

  const buildHotelBookingPayload = () => ({
    rate_id: selectedRate?.id || selectedOffer.id || null,
    search_result_id: hotel.duffel_stay_id || null,
    hotel_name: hotelInfo.name,
    address: hotelInfo.address?.cityName || 'Unknown Location',
    check_in_date: searchParams.check_in_date,
    check_out_date: searchParams.check_out_date,
    room_type: selectedRoom?.name || getRoomInfo(selectedOffer),
    guests: searchParams.adults || 1,
    total_price: totalPrice,
    currency: selectedRate?.currency || selectedOffer.price?.currency || 'USD',
    traveler_name: `${guest.given_name} ${guest.family_name}`.trim() || 'Guest',
    phone_number: guest.phone_number || '',
    email: guest.email || '',
    payment_type: selectedOffer.policies?.paymentTypeRaw || '',
  });

  const completeHotelBooking = async (paymentIntentId) => {
    setBookingLoading(true);
    setBookingError('');
    try {
      const hotelData = buildHotelBookingPayload();
      hotelData.booker_card_payment = true;
      hotelData.stripe_payment_intent_id = paymentIntentId;
      hotelData.saved_card_id = (!useNewCard && selectedSavedCardId) ? selectedSavedCardId : undefined;
      hotelData.payment_method = 'balance'; // Veyage pays Duffel via balance

      const response = await axios.post(`${apiBase}/bookings/hotels`, hotelData, { headers });
      if (response.data.success) {
        setBookingConfirmationNumber(response.data.confirmation_number || null);
        setBookingStep('success');
      } else {
        setBookingError(response.data.error || 'Booking failed.');
      }
    } catch (error) {
      console.error('Hotel booking error:', error);
      setBookingError(error.response?.data?.error || error.message || 'Unable to complete hotel booking.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleBookHotel = async () => {
    if (paymentMethod === 'booker_card') {
      // New flow: Stripe card payment for prepaid hotels
      await handleInitStripePayment();
      return;
    }
    if (paymentMethod === 'card') {
      setBookingError('Please complete the card payment form below.');
      return;
    }

    setBookingLoading(true);
    setBookingError('');

    try {
      const hotelData = buildHotelBookingPayload();
      hotelData.payment_method = paymentMethod;

      const response = await axios.post(`${apiBase}/bookings/hotels`, hotelData, { headers });

      if (response.data.success) {
        setBookingConfirmationNumber(response.data.confirmation_number || null);
        setBookingStep('success');
      } else {
        throw new Error(response.data.error || 'Booking failed');
      }
    } catch (error) {
      console.error('Hotel booking error:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Unable to complete hotel booking. Please try again or contact support.';
      setBookingError(errorMsg);
    } finally {
      setBookingLoading(false);
    }
  };

  // ---- GUEST DETAILS STEP ----
  if (bookingStep === 'guest') {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
        <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-3xl shadow-lg rounded-md bg-white mb-10">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-medium text-gray-900">Guest Details</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
          </div>

          {/* Hotel summary bar */}
          <div className="bg-indigo-50 rounded-lg p-3 mb-6 flex items-center justify-between text-sm">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mr-2">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </div>
              <span className="font-medium">{hotelInfo.name}</span>
              <span className="mx-2 text-gray-400">|</span>
              <span>{formatDate(searchParams.check_in_date).split(',')[0]} - {formatDate(searchParams.check_out_date).split(',')[0]}</span>
              <span className="mx-2 text-gray-400">|</span>
              <span>{nights} night{nights > 1 ? 's' : ''}</span>
              {selectedOffer.policies?.paymentType && (
                <>
                  <span className="mx-2 text-gray-400">|</span>
                  <span className={`font-medium ${['prepaid', 'pay_now'].includes(selectedOffer.policies.paymentTypeRaw) ? 'text-blue-600' : 'text-green-600'}`}>
                    {selectedOffer.policies.paymentType}
                  </span>
                </>
              )}
            </div>
            <div className="font-bold text-indigo-600">{currencySymbol}{displayRoomPrice}</div>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Select an executive to auto-fill or enter guest details manually.
          </p>

          <div className="border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-800">Primary Guest</h4>
              {guest._executive_id && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Auto-filled</span>
              )}
            </div>

            {/* Executive selector */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{labels.fillFromExecutive}</label>
              <select
                value={guest._executive_id || ''}
                onChange={e => fillGuestFromExecutive(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">-- Enter manually --</option>
                {executivesLoading && <option disabled>Loading executives...</option>}
                {executives.map(exec => (
                  <option key={exec.id} value={exec.id}>
                    {exec.first_name} {exec.last_name} {exec.booking_ready ? '' : '(incomplete profile)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Missing fields warning */}
            {guest._executive_id && (() => {
              const exec = executives.find(e => e.id === guest._executive_id);
              if (exec && exec.missing_fields && exec.missing_fields.length > 0) {
                return (
                  <div className="mb-3 bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800">
                    Missing from profile: {exec.missing_fields.join(', ')}. Please fill in below.
                  </div>
                );
              }
              return null;
            })()}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input type="text" value={guest.given_name} onChange={e => updateGuest('given_name', e.target.value)}
                  placeholder="Guest first name" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input type="text" value={guest.family_name} onChange={e => updateGuest('family_name', e.target.value)}
                  placeholder="Guest last name" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={guest.email} onChange={e => updateGuest('email', e.target.value)}
                  placeholder="For booking confirmation" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input type="tel" value={guest.phone_number} onChange={e => updateGuest('phone_number', e.target.value)}
                  placeholder="+1 555 123 4567" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
              </div>
            </div>
          </div>

          {/* Pricing Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h5 className="font-medium mb-2 text-sm">Booking Summary</h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Room ({nights} night{nights > 1 ? 's' : ''})</span>
                <span>{currencySymbol}{displayRoomPrice}</span>
              </div>
              {showMarkupLine && (
                <div className="flex justify-between">
                  <span>{markupLabel}</span>
                  <span>{currencySymbol}{hotelMarkup.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-2 flex justify-between font-bold">
                <span>Total</span>
                <span className="text-blue-600">{currencySymbol}{customerTotal}</span>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="mb-4">
            <h5 className="font-medium mb-2 text-sm">Payment Method</h5>
            <div className="space-y-2">
              {/* New: Booker card payment (Stripe) — primary option */}
              {allowedMethods.includes('booker_card') && (
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${paymentMethod === 'booker_card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="hotelPaymentMethod" value="booker_card"
                    checked={paymentMethod === 'booker_card'}
                    onChange={() => { setPaymentMethod('booker_card'); setPaymentIntentClientToken(null); setStripeClientSecret(null); setBookingError(''); }}
                    className="mr-3" />
                  <div>
                    <div className="font-medium text-sm">Credit / Debit Card</div>
                    <div className="text-xs text-gray-500">Pay securely with your card</div>
                  </div>
                </label>
              )}
              {/* Legacy: org credit card on file */}
              {allowedMethods.includes('credit_card') && (
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${paymentMethod === 'credit_card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="hotelPaymentMethod" value="credit_card"
                    checked={paymentMethod === 'credit_card'}
                    onChange={() => { setPaymentMethod('credit_card'); setPaymentIntentClientToken(null); setStripeClientSecret(null); setBookingError(''); }}
                    className="mr-3" />
                  <div>
                    <div className="font-medium text-sm">Company Card on File</div>
                    <div className="text-xs text-gray-500">Charged to your organization's credit card</div>
                  </div>
                </label>
              )}
              {/* Legacy: Duffel card payment */}
              {allowedMethods.includes('card') && (
                <label className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${paymentMethod === 'card' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="hotelPaymentMethod" value="card"
                    checked={paymentMethod === 'card'}
                    onChange={() => { setPaymentMethod('card'); setPaymentIntentClientToken(null); setStripeClientSecret(null); setBookingError(''); }}
                    className="mr-3" />
                  <div>
                    <div className="font-medium text-sm">Credit Card (Direct)</div>
                    <div className="text-xs text-gray-500">Pay the hotel directly with a card</div>
                  </div>
                </label>
              )}
            </div>

            {/* Saved cards selector — shown for booker_card payment method */}
            {paymentMethod === 'booker_card' && savedCards.length > 0 && !stripeClientSecret && (
              <div className="mt-3">
                <SavedCardsSelector
                  cards={savedCards.filter(c => c.card_type === 'stripe')}
                  selectedCardId={useNewCard ? null : selectedSavedCardId}
                  onSelect={(id) => { setSelectedSavedCardId(id); setUseNewCard(false); }}
                  onAddNew={() => { setUseNewCard(true); setSelectedSavedCardId(null); }}
                />
                {!useNewCard && (
                  <label className="flex items-center text-xs text-gray-500 mt-1">
                    <input type="checkbox" className="mr-2" checked={saveCardForFuture} onChange={e => setSaveCardForFuture(e.target.checked)} />
                    Save card for future bookings
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Error Message */}
          {bookingError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-md">
              <div className="flex items-center">
                <span className="text-red-400 text-xl mr-2">!</span>
                <p className="text-sm text-red-800">{bookingError}</p>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-4">
            <button onClick={() => setBookingStep('details')}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-6 rounded-md font-medium transition-colors">
              Back
            </button>
            <div className="space-y-2">
              {/* Booker card: either use saved card or init Stripe form */}
              {paymentMethod === 'booker_card' && !stripeClientSecret && (
                <button onClick={handleBookHotel} disabled={!isGuestFormValid() || bookingLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-8 rounded-md font-medium text-lg transition-colors">
                  {bookingLoading ? 'Processing...' : (
                    (!useNewCard && selectedSavedCardId)
                      ? `Pay with Saved Card - ${currencySymbol}${customerTotal}`
                      : `Proceed to Payment - ${currencySymbol}${customerTotal}`
                  )}
                </button>
              )}
              {/* Legacy: balance/credit_card/ach */}
              {(paymentMethod === 'balance' || paymentMethod === 'credit_card' || paymentMethod === 'ach') && (
                <button onClick={handleBookHotel} disabled={!isGuestFormValid() || bookingLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-8 rounded-md font-medium text-lg transition-colors">
                  {bookingLoading ? 'Booking...' : `Confirm & Book - ${currencySymbol}${customerTotal}`}
                </button>
              )}
              {/* Legacy: Duffel card */}
              {paymentMethod === 'card' && !paymentIntentClientToken && (
                <button onClick={handleSelectCardPayment} disabled={!isGuestFormValid() || bookingLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-3 px-8 rounded-md font-medium text-lg transition-colors">
                  {bookingLoading ? 'Initializing payment...' : `Proceed to Card Payment - ${currencySymbol}${customerTotal}`}
                </button>
              )}
            </div>
          </div>

          {/* Stripe card form for booker card payment */}
          {paymentMethod === 'booker_card' && stripeClientSecret && (
            <div className="mt-6">
              <h5 className="font-medium mb-3">Enter Card Details</h5>
              <StripeCardForm
                clientSecret={stripeClientSecret}
                onSuccess={handleStripePaymentSuccess}
                onError={(err) => setBookingError(`Payment failed: ${err?.message || 'Unknown error'}`)}
                buttonLabel={`Pay ${currencySymbol}${customerTotal}`}
                loading={bookingLoading}
              />
              {saveCardForFuture && (
                <p className="text-xs text-gray-500 mt-2 text-center">Your card will be saved for future bookings</p>
              )}
            </div>
          )}

          {/* Legacy: Duffel card payment form */}
          {paymentMethod === 'card' && paymentIntentClientToken && (
            <div className="mt-6">
              <h5 className="font-medium mb-3">Enter Card Details</h5>
              <DuffelPaymentsElement
                clientToken={paymentIntentClientToken}
                onSuccess={handleCardPaymentSuccess}
                onError={(err) => setBookingError(`Card payment failed: ${err?.message || 'Unknown error'}`)}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (bookingStep === 'success') {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <span className="text-green-600 text-2xl">✓</span>
            </div>
            <h3 className="text-2xl font-medium text-gray-900 mb-4">Hotel Booking Confirmed!</h3>
            {bookingConfirmationNumber && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 inline-block">
                <div className="text-xs text-gray-500 uppercase font-medium mb-1">Confirmation Reference</div>
                <div className="text-2xl font-mono font-bold text-blue-600 tracking-wider">{bookingConfirmationNumber}</div>
              </div>
            )}
            <p className="text-gray-600 mb-6">
              Your hotel reservation has been added to your travel itinerary. You can view and manage it in the Hotels section.
            </p>
            <div className="space-y-3">
              <button 
                onClick={onClose}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-md font-medium text-lg transition-colors"
              >
                View My Hotels
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 pb-4">
            <h3 className="text-2xl font-medium text-gray-900">
              Complete Your Hotel Booking
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Hotel Details */}
          <div className="py-6">
            <div className="flex space-x-6">
              {/* Hotel Image */}
              <div className="w-48 h-32 flex-shrink-0">
                <img
                  src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=200&h=130&fit=crop&auto=format"
                  alt={hotelInfo.name}
                  className="w-full h-full object-cover rounded-md"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEzMCIgdmlld0JveD0iMCAwIDIwMCAxMzAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTMwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iNzAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5QjlCOUIiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCI+SG90ZWwgSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo=';
                  }}
                />
              </div>

              {/* Hotel Information */}
              <div className="flex-1">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  {hotelInfo.name}
                </h4>

                {hotelInfo.address && (
                  <div className="flex items-center text-sm text-gray-600 mb-3">
                    <span className="mr-1">📍</span>
                    <span>{hotelInfo.address.cityName}</span>
                    {hotelInfo.address.countryCode && (
                      <span>, {hotelInfo.address.countryCode}</span>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Check-in:</span>
                    <div className="text-gray-600">
                      {formatDate(searchParams.check_in_date)}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">Check-out:</span>
                    <div className="text-gray-600">
                      {formatDate(searchParams.check_out_date)}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">Guests:</span>
                    <div className="text-gray-600">
                      {searchParams.adults || 1} Adult{(searchParams.adults || 1) > 1 ? 's' : ''}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">Rooms:</span>
                    <div className="text-gray-600">
                      {searchParams.room_quantity || 1} Room{(searchParams.room_quantity || 1) > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Summary */}
              <div className="w-64 bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-3">Booking Summary</h5>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Room type:</span>
                    <span className="font-medium text-right ml-2" style={{maxWidth: '60%'}}>{selectedRoom ? (selectedRoom.name || 'Room') : getRoomInfo(selectedOffer)}</span>
                  </div>
                  <div className="flex justify-between">
                    <>
                      <span>{nights} night{nights > 1 ? 's' : ''}:</span>
                      <span className="font-medium">{currencySymbol}{displayRoomPrice}</span>
                    </>
                  </div>
                  {showMarkupLine && (
                    <div className="flex justify-between">
                      <span>{markupLabel}:</span>
                      <span className="font-medium">{currencySymbol}{hotelMarkup.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-300 pt-2 mt-3">
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total:</span>
                      <span className="text-blue-600">{currencySymbol}{customerTotal}</span>
                    </div>
                  </div>
                  {selectedOffer.policies?.paymentType && (
                    <div className="flex justify-between text-xs">
                      <span>Payment:</span>
                      <span className={`font-medium ${['prepaid', 'pay_now'].includes(selectedOffer.policies.paymentTypeRaw) ? 'text-blue-600' : 'text-green-600'}`}>
                        {selectedOffer.policies.paymentType}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  <div className="flex items-center text-xs text-green-600 mb-2">
                    <span className="mr-1">✓</span>
                    Includes taxes & fees
                  </div>
                  {selectedOffer.policies?.cancellation && Object.keys(selectedOffer.policies.cancellation).length > 0 && (
                    <div className="flex items-center text-xs text-green-600">
                      <span className="mr-1">✓</span>
                      Free cancellation
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Room & Rate Selection */}
          {ratesLoading && (
            <div className="py-6 border-t border-gray-200 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-sm text-gray-600">Loading all available rooms and rates...</span>
            </div>
          )}
          {ratesError && !allRooms && (
            <div className="py-4 border-t border-gray-200">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                {ratesError}
              </div>
            </div>
          )}
          {allRooms && allRooms.length > 0 && (
            <div className="py-4 border-t border-gray-200">
              <h5 className="font-semibold text-gray-900 mb-3">Select a Room</h5>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {allRooms.map((room, roomIdx) => {
                  const isRoomSelected = roomIdx === selectedRoomIndex;
                  return (
                    <div key={room.id || roomIdx} className={`border rounded-lg overflow-hidden ${isRoomSelected ? 'border-blue-500' : 'border-gray-200'}`}>
                      {/* Room header */}
                      <div
                        onClick={() => { setSelectedRoomIndex(roomIdx); setSelectedRateIndex(0); }}
                        className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                          isRoomSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            isRoomSelected ? 'border-blue-500' : 'border-gray-300'
                          }`}>
                            {isRoomSelected && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{room.name || 'Room'}</div>
                            {room.description && (
                              <div className="text-xs text-gray-500 mt-0.5">{room.description}</div>
                            )}
                            {room.beds && room.beds.length > 0 && (
                              <div className="text-xs text-gray-400 mt-0.5">
                                {room.beds.map(b => `${b.count || 1} ${(b.type || 'bed').replace(/_/g, ' ')}`).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                        {room.rates?.length > 0 && (() => {
                          const firstRate = room.rates[0];
                          const firstRateAmount = parseFloat(firstRate.total_amount || 0);
                          const firstRatePrepaid = ['prepaid', 'pay_now'].includes(firstRate.payment_type);
                          const firstRateDisplay = firstRatePrepaid
                            ? (firstRateAmount + calcMarkupForAmount(firstRateAmount, true)).toFixed(2)
                            : firstRateAmount.toFixed(2);
                          return (
                            <div className="text-right text-sm">
                              <span className="text-gray-500">from </span>
                              <span className="font-semibold text-gray-900">
                                {(firstRate.currency === 'EUR' ? '\u20AC' : '$')}
                                {firstRateDisplay}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      {/* Rates for selected room */}
                      {isRoomSelected && room.rates && room.rates.length > 0 && (
                        <div className="border-t border-gray-100 bg-gray-50 p-3 space-y-2">
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Available Rates</div>
                          {room.rates.map((rate, rateIdx) => {
                            const isRateSelected = rateIdx === selectedRateIndex;
                            const rateCurrency = rate.currency === 'EUR' ? '\u20AC' : '$';
                            const isPrepaid = ['prepaid', 'pay_now'].includes(rate.payment_type);
                            // Determine cancellation policy from cancellation_timeline
                            const timeline = rate.cancellation_timeline || [];
                            const rawAmount = parseFloat(rate.total_amount || 0);
                            const totalAmount = isPrepaid
                              ? rawAmount + calcMarkupForAmount(rawAmount, true)
                              : rawAmount;
                            let cancellationLabel = '';
                            let cancellationColor = '';
                            let cancellationDetail = '';
                            if (timeline.length > 0) {
                              const firstEntry = timeline[0];
                              const refundAmount = parseFloat(firstEntry.refund_amount || 0);
                              if (refundAmount >= totalAmount) {
                                cancellationLabel = 'Free cancellation';
                                cancellationColor = 'text-green-600';
                                const beforeDate = firstEntry.before ? new Date(firstEntry.before) : null;
                                if (beforeDate) {
                                  cancellationDetail = `before ${beforeDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                                }
                              } else if (refundAmount > 0) {
                                cancellationLabel = 'Partial refund';
                                cancellationColor = 'text-yellow-600';
                                const beforeDate = firstEntry.before ? new Date(firstEntry.before) : null;
                                if (beforeDate) {
                                  cancellationDetail = `${rateCurrency}${refundAmount.toFixed(2)} back before ${beforeDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                                }
                                // If no cancel-by date, don't show refund amount (terms unclear)
                              } else {
                                cancellationLabel = 'Non-refundable';
                                cancellationColor = 'text-red-500';
                              }
                            } else if (rate.cancellation && Object.keys(rate.cancellation).length > 0) {
                              cancellationLabel = 'Free cancellation';
                              cancellationColor = 'text-green-600';
                            } else {
                              cancellationLabel = 'Non-refundable';
                              cancellationColor = 'text-red-500';
                            }
                            // Loyalty programme
                            const loyaltyProgramme = rate.supported_loyalty_programme
                              ? rate.supported_loyalty_programme.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                              : '';
                            return (
                              <div
                                key={rate.id || rateIdx}
                                onClick={() => setSelectedRateIndex(rateIdx)}
                                className={`flex items-center justify-between p-2.5 rounded-md border cursor-pointer transition-colors ${
                                  isRateSelected
                                    ? 'border-blue-400 bg-white shadow-sm'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                              >
                                <div className="flex items-center space-x-2">
                                  <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${
                                    isRateSelected ? 'border-blue-500' : 'border-gray-300'
                                  }`}>
                                    {isRateSelected && <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>}
                                  </div>
                                  <div>
                                    <div className="flex items-center space-x-2 text-xs">
                                      {rate.payment_type_label && (
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                                          isPrepaid ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                        }`}>
                                          {rate.payment_type_label}
                                        </span>
                                      )}
                                      {rate.board_type && (
                                        <span className="text-gray-500">{rate.board_type.replace(/_/g, ' ')}</span>
                                      )}
                                      {cancellationLabel && (
                                        <span className={cancellationColor}>{cancellationLabel}</span>
                                      )}
                                    </div>
                                    {(cancellationDetail || loyaltyProgramme) && (
                                      <div className="flex items-center space-x-2 text-xs mt-0.5 ml-0.5">
                                        {cancellationDetail && (
                                          <span className="text-gray-400">{cancellationDetail}</span>
                                        )}
                                        {loyaltyProgramme && (
                                          <span className="text-purple-600">{loyaltyProgramme}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {rateCurrency}{totalAmount.toFixed(2)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Fallback: show initial offers when rates not loaded */}
          {!allRooms && !ratesLoading && offers.length > 1 && (
            <div className="py-4 border-t border-gray-200">
              <h5 className="font-semibold text-gray-900 mb-3">Select a Rate</h5>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {offers.map((offer, idx) => {
                  // offer.price.total is already the total stay amount
                  const offerTotal = offer.price?.total ? parseFloat(offer.price.total).toFixed(2) : 'N/A';
                  const offerCurrency = offer.price?.currency === 'EUR' ? '\u20AC' : '$';
                  const paymentType = offer.policies?.paymentType || '';
                  const isPrepaid = ['prepaid', 'pay_now'].includes(offer.policies?.paymentTypeRaw);
                  const isSelected = idx === selectedOfferIndex;

                  return (
                    <div
                      key={offer.id || idx}
                      onClick={() => setSelectedOfferIndex(idx)}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-blue-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getRoomInfo(offer)}
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            {paymentType && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                isPrepaid
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {paymentType}
                              </span>
                            )}
                            {offer.boardType && (
                              <span className="text-gray-400">{offer.boardType.replace(/_/g, ' ')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900">
                          {offerCurrency}{offerTotal}
                        </div>
                        <div className="text-xs text-gray-500">
                          {offerCurrency}{offer.price?.total ? (parseFloat(offer.price.total) / nights).toFixed(2) : '0'}/night
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Error Message */}
          {bookingError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-md">
              <div className="flex items-center">
                <span className="text-red-400 text-xl mr-2">⚠️</span>
                <div>
                  <p className="text-sm text-red-800">{bookingError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>

            <div className="space-x-4">
              <button
                onClick={() => setBookingStep('guest')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-md font-medium text-lg transition-colors"
              >
                Continue - Guest Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSearch;